import axios, { AxiosAdapter, AxiosError, AxiosInstance } from 'axios';

import { config } from '@/config/environment';
import {
  ApiUnavailableError,
  ConditionalRequestOptions,
  ConditionalResult,
  LONG_REQUEST_TIMEOUT_MS,
  parseRetryAfter,
  REGISTRY_ORDER_TIMEOUT_MS,
  REQUEST_TIMEOUT_MS,
  RequestOptions,
  UPLOAD_TIMEOUT_MS,
} from '@/services/http';
import {
  detailMessage,
  registryOrderErrorFor,
  registryOrderTransportError,
} from '@/services/registry-order-error';
import {
  ApiBackofficeTemplate,
  ApiReportConfig,
  ApiReportConfigSummary,
  ApiScopeComponent,
  ApiScopeComponentCapability,
  ApiSubject,
  BackofficeTemplate,
  BindingCatalog,
  BindingMap,
  BindingValidationReport,
  ConfigExportPayload,
  DataRequirementEnums,
  DependencyValidationRequest,
  DependencyValidationResponse,
  ImportMissingScopeComponentsDetail,
  ImportOnConflict,
  RegionTree,
  RenderingContextCatalogue,
  ReportConfig,
  ReportConfigComponentBase,
  ReportConfigCreateRequest,
  ReportConfigSummary,
  ReportConfigUpdateRequest,
  ScopeComponent,
  ScopeComponentCapability,
  ScopeComponentCapabilityCreateRequest,
  ScopeComponentCapabilityUpdateRequest,
  ScopeComponentCreateRequest,
  ScopeComponentUpdateRequest,
  Subject,
  SubjectScopeType,
  SubjectType,
  TemplateAnchorRequest,
  TemplateAnchorsResponse,
  TemplateContextSchema,
  TemplateDocxUploadResponse,
  TemplatePreviewRequest,
  TemplatePreviewResponse,
  TemplateValidateRequest,
  TemplateValidateResponse,
  transformToBackofficeTemplate,
  transformToReportConfig,
  transformToReportConfigSummary,
  transformToScopeComponent,
  transformToScopeComponentCapability,
} from '@/types';
import {
  ApiConversationAttachment,
  ApiReportRequestAttachment,
  ApiReportRequestSource,
  ConversationAttachment,
  ConversationUploadContext,
  ReportRequestAttachment,
  ReportRequestSource,
  transformToConversationAttachment,
  transformToReportRequestAttachment,
  transformToReportRequestSource,
} from '@/types/attachment';
import {
  ApiCaseActivity,
  ApiCaseDetail,
  CaseActivity,
  CaseDetail,
  transformToCaseActivity,
  transformToCaseDetail,
} from '@/types/case';
import {
  ApiCaseDashboardResponse,
  CaseDashboardResponse,
  DashboardView,
  transformToCaseDashboard,
} from '@/types/caseDashboard';
import {
  ChatMessage,
  ChatSendRequest,
  ChatThread,
  ChatThreadKind,
  transformToChatMessage,
  transformToChatThread,
} from '@/types/chat';
import {
  BackofficeUnreadableError,
  transformToContractDetail,
  transformToContractReconciliation,
  transformToContractSummary,
  type ApiContractDetail,
  type ApiContractReconciliation,
  type ApiContractSummary,
  type ContractDetail,
  type ContractReconciliation,
  type ContractSummary,
} from '@/types/client-contract';
import { CodenameRenameResult, transformToCodenameRenameResult } from '@/types/codename';
import {
  transformToCommercialChange,
  transformToReportBillingStatus,
  type ApiCommercialChangesResponse,
  type ApiReportBillingStatus,
  type CommercialChange,
  type ReportBillingStatus,
} from '@/types/commercial-change';
import {
  DirectConfirmMode,
  DirectConfirmRequestBody,
  DirectExtractRequestBody,
  DirectExtractResponse,
  DirectRequestDraft,
  IntakeClassification,
} from '@/types/direct-request';
import {
  transformToEconomicsRefreshResult,
  transformToEconomicsSummary,
  transformToProjectEconomicsDetail,
  transformToProjectEconomicsPage,
  transformToUnattributedCosts,
  type ApiEconomicsRefreshResult,
  type ApiEconomicsSummary,
  type ApiProjectEconomicsDetail,
  type ApiProjectEconomicsPage,
  type ApiUnattributedCosts,
  type DisplayCurrency,
  type EconomicsQuery,
  type EconomicsRefreshResult,
  type ConsumptionSource as EconomicsSource,
  type EconomicsSummary,
  type ProjectEconomicsDetail,
  type ProjectEconomicsPage,
  type ProjectEconomicsQuery,
  type UnattributedCosts,
} from '@/types/economics';
import {
  ApiClientEnquiry,
  ApiEnquiryDetail,
  ApiEnquirySummary,
  ApiEnquiryTeamResolution,
  ClientEnquiry,
  ClientEnquiryCreatePayload,
  EnquiryConvertResponse,
  EnquiryDetail,
  EnquiryResolution,
  EnquiryResolveRequest,
  EnquiryStage,
  EnquirySummary,
  EnquiryTeamResolution,
  transformToClientEnquiry,
  transformToEnquiryDetail,
  transformToEnquirySummary,
  transformToEnquiryTeamResolution,
} from '@/types/enquiry';
import {
  EnquiryExtractConvertRequest,
  ExtractionSourceSet,
  ExtractReportRequestsResponse,
} from '@/types/extraction';
import {
  GoogleDocsExportResponse,
  TriggerExportRequest,
  TriggerExportResponse,
} from '@/types/google-docs';
import {
  ApiInboxBrowseResponse,
  InboxAssignRequest,
  InboxBrowseParams,
  InboxDismissRequest,
  InboxGroupedParams,
  InboxGroupedResponse,
  InboxItem,
  InboxListParams,
  InboxListResponse,
  InboxResolveRequest,
  InboxSnoozeRequest,
  transformToInboxGrouped,
  transformToInboxItem,
} from '@/types/inbox';
import {
  ApiMatchReviewList,
  ApiMatchReviewSubmitResponse,
  ApiRemediationReview,
  MatchReviewDecision,
  MatchReviewList,
  MatchReviewSubmitResponse,
  RemediationReview,
  transformToMatchReviewList,
  transformToMatchReviewSubmitResponse,
  transformToRemediationReview,
} from '@/types/match-review';
import {
  ApiMonitoredSubjectPage,
  ApiMonitoringAlertPage,
  ApiMonitoringEnrolmentOptions,
  ApiMonitoringEnrolmentResult,
  ApiMonitoringEntitlement,
  ApiMonitoringEntitlementChange,
  ApiSubjectCancellation,
  ApiSubjectRegistration,
  ApiTeamSubjectDetail,
  ApiTeamSubjectPage,
  MonitoredSubjectPage,
  MonitoredSubjectQuery,
  MonitoringAlertPage,
  MonitoringAlertQuery,
  MonitoringCancellationReason,
  MonitoringEnrolmentOptions,
  MonitoringEnrolmentResult,
  MonitoringEntitlement,
  MonitoringEntitlementAction,
  MonitoringEntitlementChange,
  SubjectCancellation,
  SubjectRegistration,
  TeamSubjectDetail,
  TeamSubjectPage,
  TeamSubjectQuery,
  transformToMonitoredSubjectPage,
  transformToMonitoringAlertPage,
  transformToMonitoringEnrolmentOptions,
  transformToMonitoringEnrolmentResult,
  transformToMonitoringEntitlement,
  transformToMonitoringEntitlementChange,
  transformToSubjectCancellation,
  transformToSubjectRegistration,
  transformToTeamSubjectDetail,
  transformToTeamSubjectPage,
} from '@/types/monitoring';
import {
  ApiBackofficeAssignment,
  ApiRegistryPartnerSync,
  ApiReportAssignments,
  BackofficeAssignment,
  BackofficeAssignmentUpdate,
  RegistryFeeAdjustmentRequest,
  RegistryPartnerSync,
  ReportAssignments,
  transformToBackofficeAssignment,
  transformToRegistryPartnerSync,
  transformToReportAssignments,
} from '@/types/np-assignments';
import {
  ApiProfileReviewList,
  ApiProfileReviewSubmitResponse,
  ApiSubjectProfile,
  ProfileDecision,
  ProfileReviewList,
  ProfileReviewSubmitResponse,
  SubjectProfile,
  transformToProfileReviewList,
  transformToProfileReviewSubmitResponse,
  transformToSubjectProfile,
} from '@/types/profile-review';
import {
  toApiProviderAgreementWrite,
  toApiProviderRateCreate,
  toApiProviderRateUpdate,
  transformToProviderAgreement,
  transformToProviderRate,
  type ApiProviderAgreement,
  type ApiProviderRate,
  type ProviderAgreement,
  type ProviderAgreementInput,
  type ProviderRate,
  type ProviderRateInput,
  type ProviderRateUpdate,
} from '@/types/provider-cost';
import {
  ApiRegistryAttention,
  ApiRegistryCompanySearchResponse,
  ApiRegistryDigestAddedToReportResponse,
  ApiRegistryDigestAddToReportPreview,
  ApiRegistryDigestDocx,
  ApiRegistryDigestMarkdown,
  ApiRegistryDigestResponse,
  ApiRegistryDigestStatus,
  ApiRegistryInboundPage,
  ApiRegistryInboundRow,
  ApiRegistryOrderConversation,
  ApiRegistryOrderDetail,
  ApiRegistryOrderList,
  ApiRegistryOrderQueuePage,
  ApiRegistryProductsResponse,
  RegistryAttention,
  RegistryCompanySearchParams,
  RegistryCompanySearchResult,
  RegistryDigestAddedToReport,
  RegistryDigestAddToReportPreview,
  RegistryDigestDocx,
  RegistryDigestMarkdown,
  RegistryDigestResult,
  RegistryDigestStatus,
  RegistryInboundPage,
  RegistryInboundQuery,
  RegistryInboundRow,
  RegistryOrderCloseRequest,
  RegistryOrderConversation,
  RegistryOrderCreateRequest,
  RegistryOrderDetail,
  RegistryOrderList,
  RegistryOrderQueuePage,
  RegistryOrderQueueQuery,
  RegistryOrderQuoteRecordRequest,
  RegistryOrderReplyRequest,
  RegistryOrderStatus,
  RegistryProduct,
  transformToRegistryAttention,
  transformToRegistryCompanySearchResult,
  transformToRegistryConversation,
  transformToRegistryDigestAddedToReport,
  transformToRegistryDigestAddToReportPreview,
  transformToRegistryDigestMarkdown,
  transformToRegistryDigestResult,
  transformToRegistryDigestStatus,
  transformToRegistryInboundPage,
  transformToRegistryInboundRow,
  transformToRegistryOrderDetail,
  transformToRegistryOrderList,
  transformToRegistryOrderQueuePage,
  transformToRegistryProduct,
} from '@/types/registry-documents';
import {
  ApiRelatedEntitiesResponse,
  ApiRelatedEntity,
  ApiRelatedEntityDispositionResponse,
  RelatedEntitiesResult,
  RelatedEntity,
  RelatedEntityAction,
  RelatedEntityDispositionResult,
  transformToRelatedEntities,
  transformToRelatedEntity,
  transformToRelatedEntityDisposition,
} from '@/types/related-entity';
import {
  ApiReportApproval,
  ApiReportSummary,
  BackofficeFile,
  DocxIngestResponse,
  ReportApproval,
  ReportEscalationRequest,
  ReportEscalationResponse,
  ReportLevel,
  ReportResponse,
  ReportRunFailure,
  ReportStatusResponse,
  ReportSummary,
  ReportVersionSummary,
  RetryFailedRunResponse,
  RetryRemediationResponse,
  transformToReportApproval,
  transformToReportSummary,
} from '@/types/report';
import { BlendStatusResponse, StartBlendRequest, StartBlendResponse } from '@/types/report-blender';
import type { FeedbackAnchors } from '@/types/report-feedback';
import {
  ApiCaseParticipant,
  ApiClientReportRequestSummary,
  ApiClientTeamMember,
  ApiReportRequestDetail,
  ApiReportRequestSummary,
  ApiTriageSplitResponse,
  CaseParticipant,
  ClientReportRequestSummary,
  ClientRequestStatusFilter,
  ClientTeamMember,
  PricingUpdateRequest,
  ReportRequestDetail,
  ReportRequestSummary,
  transformToCaseParticipant,
  transformToClientReportRequestSummary,
  transformToClientTeamMember,
  transformToReportRequestDetail,
  transformToReportRequestSummary,
  TriageAddSubjectRequest,
  TriageAdvanceRequest,
  TriageEditSubjectRequest,
  TriageGatesResponse,
  TriageInitialUpdateRequest,
  TriageManualResolveRequest,
  TriageRejectRequest,
  TriageRevertRequest,
  TriageStage,
  TriageStartResponse,
} from '@/types/report-request';
import {
  SelectionResponse,
  SubjectCandidateResponse,
  SubjectSelection,
  SubjectWithCandidates,
} from '@/types/subject-candidate';
import {
  ApiNotificationAddress,
  ApiNotificationRule,
  ApiResolvedIntake,
  ApiTeamMember,
  ApiTeamNotifications,
  ApiTeamSettingsRow,
  ConfirmedAddress,
  IntakeMode,
  NotificationAddress,
  NotificationEvent,
  NotificationRule,
  NotificationRuleInput,
  ResolvedIntake,
  TeamMember,
  TeamNotifications,
  TeamSettingsRow,
  transformToNotificationAddress,
  transformToNotificationRule,
  transformToNotificationRuleInput,
  transformToResolvedIntake,
  transformToTeamMember,
  transformToTeamNotifications,
  transformToTeamSettingsRow,
} from '@/types/team-settings';

// Report Feedback interfaces
export interface ReportFeedbackCreate {
  feedback: string;
  context?: string | null;
  anchors?: FeedbackAnchors;
}

export interface ReportFeedbackResponse {
  id: string;
  report_id: string;
  feedback: string;
  context: string | null;
  created_at: string | null;
}

export interface ReportContentVersionResponse {
  id: string;
  report_id: string;
  version: number;
  content: string;
  feedback_id: string | null;
  created_at: string | null;
  /** diff-match-patch segments [[op, text], ...]; null for v1 and markdown reports. */
  diff: [number, string][] | null;
}

// Long-running processing statuses (escalations, report blending)
export enum ProcessingStatus {
  Pending = 'pending',
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
  Interrupted = 'interrupted',
}

// Helper function to transform API response to Subject interface
export function transformToSubject(apiSubject: ApiSubject): Subject {
  return {
    id: apiSubject.id,
    subjectName: apiSubject.subject_name,
    subjectType: apiSubject.subject_type,
    jurisdictions: apiSubject.jurisdictions,
    reference: apiSubject.reference,
    externalProjectId: apiSubject.external_project_id,
    additionalInfo: apiSubject.additional_info,
    teamId: apiSubject.team_id,
    createdAt: apiSubject.created_at,
    updatedAt: apiSubject.updated_at,
  };
}

export interface CreateSubjectRequest {
  subject_name: string;
  subject_type: SubjectType;
  jurisdiction_codes: string[];
  reference?: string;
  additional_info?: Record<string, unknown> | null;
  report_config_id?: string;
}

export interface EscalatedSubject {
  id: string;
  subject_name: string;
  subject_type: string;
  thread_id: string;
  scope: SubjectScopeType;
  worker_threads: string[];
}

export interface EscalationProcessingStatusResponse {
  thread_id: string;
  status: ProcessingStatus;
  message: string;
  project_description?: string | null;
  error?: string | null;
  subject_name?: string | null;
  subject_type?: string | null;
  worker_threads: string[] | null;
}

/**
 * Structured 409 conflict raised by the API. Carries the response
 * body's `detail` object so callers can read fields like
 * `missing_item_ids` (Phase 05 NP-selection precondition) without
 * re-parsing the message string.
 */
export class ApiConflictError extends Error {
  readonly detail: Record<string, unknown>;

  constructor(message: string, detail: Record<string, unknown>) {
    super(message);
    this.name = 'ApiConflictError';
    this.detail = detail;
  }
}

/**
 * Extract the ``filename`` value from a ``Content-Disposition`` header.
 * Handles both ``filename="..."`` (quoted) and ``filename=...`` (bare).
 * Returns ``null`` when the header is absent, malformed, or stripped by
 * CORS — callers should synthesise a fallback in that case.
 */
function parseContentDispositionFilename(header: string | undefined): string | null {
  if (!header) {
    return null;
  }
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match ? decodeURIComponent(match[1].trim()) : null;
}

/**
 * Thrown for 400 responses whose ``detail`` is a structured object the
 * caller needs to render (e.g. the import endpoint's missing-scope-
 * components list). Plain string ``detail`` 400s still throw a regular
 * ``Error`` via the global interceptor.
 */
export class ApiValidationError extends Error {
  readonly detail: Record<string, unknown>;

  constructor(message: string, detail: Record<string, unknown>) {
    super(message);
    this.name = 'ApiValidationError';
    this.detail = detail;
  }
}

class VnextApiClient {
  private readonly instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: config.vnextApiUrl,
      withCredentials: true,
      timeout: REQUEST_TIMEOUT_MS,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Demo mode only (src/demo): swap the transport for the canned-data
   * adapter. Every method, transform and interceptor keeps running — only
   * the network goes away. The returned function puts the previous
   * transport back, for a story that answers the client's reads only while
   * it is mounted.
   */
  installDemoAdapter(adapter: AxiosAdapter): () => void {
    const previous = this.instance.defaults.adapter;
    this.instance.defaults.adapter = adapter;
    return () => {
      this.instance.defaults.adapter = previous;
    };
  }

  private setupInterceptors() {
    // Response interceptor to handle errors
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<{ error?: string; detail?: string }>) => {
        // Handle 401 unauthorized
        if (error.response?.status === 401) {
          throw new Error('Unauthorized');
        }

        // Handle 409 conflict specifically. Phase 05 introduced
        // structured 409 bodies of the shape
        // {detail: {message: "...", missing_item_ids: [...]}} so the
        // caller can render targeted UI (e.g. deep-link to plan
        // items with no NP attached). When the body is shaped that
        // way we throw an ApiConflictError carrying the structured
        // payload; otherwise fall back to the legacy string-detail
        // path so older 409s still surface a sensible message.
        if (error.response?.status === 409) {
          const data = error.response.data as
            | { error?: string; detail?: string | { message?: string; [k: string]: unknown } }
            | undefined;
          const detail = data?.detail;
          if (detail && typeof detail === 'object') {
            throw new ApiConflictError((detail.message as string) ?? 'Conflict', detail);
          }
          const errorMessage =
            data?.error ?? (typeof detail === 'string' ? detail : 'This subject already exists');
          throw new Error(errorMessage);
        }

        // 503 is the load signal: surface it as its own error (with any
        // Retry-After) so polling backs off rather than flailing. No
        // blanket retries here — that would hide the signal and double
        // up with the poll backoff.
        if (error.response?.status === 503) {
          throw new ApiUnavailableError(parseRetryAfter(error.response.headers?.['retry-after']));
        }

        // Handle 400+ errors with error message. FastAPI 422s carry
        // `detail` as an ARRAY of objects — anything non-string must be
        // stringified or the Error message renders as "[object Object]".
        // A `{message}` object (the assignment endpoints' shape) is already
        // written for a person, so show its message rather than the JSON.
        if (error.response && error.response.status >= 400) {
          const rawDetail: unknown = error.response.data?.error ?? error.response.data?.detail;
          const objectMessage =
            rawDetail != null && typeof rawDetail === 'object' && !Array.isArray(rawDetail)
              ? (rawDetail as { message?: unknown }).message
              : undefined;
          let errorMessage = `Error ${error.response.status}`;
          if (typeof rawDetail === 'string') {
            errorMessage = rawDetail;
          } else if (typeof objectMessage === 'string') {
            errorMessage = objectMessage;
          } else if (rawDetail != null) {
            errorMessage = JSON.stringify(rawDetail);
          }

          // For 500 errors, only show details in development
          if (error.response.status >= 500 && !config.isDevelopment) {
            throw new Error('Server error');
          }

          throw new Error(errorMessage);
        }

        // Network or other errors
        const rejectionError = error instanceof Error ? error : new Error(String(error));
        throw rejectionError;
      }
    );
  }

  // ---- Direct requests (self-service extract → review → confirm) ----

  /** Mint the draft ReportRequest + chat thread that anchor file uploads
   *  for a direct request still being composed. Files are then uploaded
   *  via uploadConversationAttachment against the returned thread. */
  async createDirectRequestDraft(teamId?: string | null): Promise<DirectRequestDraft> {
    const response = await this.instance.post(
      '/direct-requests/drafts',
      teamId ? { team_id: teamId } : {}
    );
    return response.data as DirectRequestDraft;
  }

  /** Run extraction over typed text + uploaded files; returns editable
   *  candidates. Creates nothing server-side. */
  async extractDirectRequest(body: DirectExtractRequestBody): Promise<DirectExtractResponse> {
    const response = await this.instance.post('/direct-requests/extract', body, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return response.data as DirectExtractResponse;
  }

  // ---- Team intake settings ----

  /** The caller's team's resolved intake (admins may pass a teamId). */
  async getCurrentIntake(teamId?: string): Promise<ResolvedIntake> {
    const response = await this.instance.get('/team-settings/current', {
      params: teamId ? { team_id: teamId } : undefined,
    });
    return transformToResolvedIntake(response.data as ApiResolvedIntake);
  }

  /** Admin: every team with an explicit settings row. */
  async listTeamSettings(): Promise<TeamSettingsRow[]> {
    const response = await this.instance.get('/team-settings/');
    return (response.data as ApiTeamSettingsRow[]).map(transformToTeamSettingsRow);
  }

  /** Admin: set a team's intake mode (upsert). */
  async putTeamSettings(teamId: string, intakeMode: IntakeMode): Promise<TeamSettingsRow> {
    const response = await this.instance.put(`/team-settings/${teamId}`, {
      intake_mode: intakeMode,
    });
    return transformToTeamSettingsRow(response.data as ApiTeamSettingsRow);
  }

  /** Admin: the notifications a team can choose recipients for. */
  async listNotificationEvents(): Promise<NotificationEvent[]> {
    const response = await this.instance.get('/team-settings/notification-events');
    return response.data as NotificationEvent[];
  }

  /** Admin: a team's notification rules and copy addresses. */
  async getTeamNotifications(teamId: string): Promise<TeamNotifications> {
    const response = await this.instance.get(`/team-settings/${teamId}/notifications`);
    return transformToTeamNotifications(response.data as ApiTeamNotifications);
  }

  /** Admin: the team's client users, for choosing who to notify. */
  async listTeamMembers(teamId: string): Promise<TeamMember[]> {
    const response = await this.instance.get(`/team-settings/${teamId}/members`);
    return (response.data as ApiTeamMember[]).map(transformToTeamMember);
  }

  /** Admin: set the team default rule (`ruleKey` = `default`) or one
   *  notification's rule (`ruleKey` = its dotted type). */
  async putNotificationRule(
    teamId: string,
    ruleKey: string,
    input: NotificationRuleInput
  ): Promise<NotificationRule> {
    const response = await this.instance.put(
      `/team-settings/${teamId}/notifications/rules/${encodeURIComponent(ruleKey)}`,
      transformToNotificationRuleInput(input)
    );
    return transformToNotificationRule(response.data as ApiNotificationRule);
  }

  /** Admin: remove a rule so the notification falls back a level. */
  async deleteNotificationRule(teamId: string, ruleKey: string): Promise<void> {
    await this.instance.delete(
      `/team-settings/${teamId}/notifications/rules/${encodeURIComponent(ruleKey)}`
    );
  }

  /** Admin: register a copy address; its owner is emailed a confirmation link. */
  async registerNotificationAddress(teamId: string, email: string): Promise<NotificationAddress> {
    const response = await this.instance.post(`/team-settings/${teamId}/notifications/addresses`, {
      email,
    });
    return transformToNotificationAddress(response.data as ApiNotificationAddress);
  }

  /** Admin: email a fresh confirmation link to an unconfirmed address. */
  async resendNotificationAddress(teamId: string, addressId: string): Promise<NotificationAddress> {
    const response = await this.instance.post(
      `/team-settings/${teamId}/notifications/addresses/${addressId}/resend`
    );
    return transformToNotificationAddress(response.data as ApiNotificationAddress);
  }

  /** Admin: remove a copy address; rules naming it stop copying to it. */
  async deleteNotificationAddress(teamId: string, addressId: string): Promise<void> {
    await this.instance.delete(`/team-settings/${teamId}/notifications/addresses/${addressId}`);
  }

  /** Public (no login): which address a confirmation token would confirm.
   *  Changes nothing. The token travels in the body, never the URL. */
  async previewNotificationAddress(token: string): Promise<ConfirmedAddress> {
    const response = await this.instance.post('/notification-addresses/confirm/preview', {
      token,
    });
    return response.data as ConfirmedAddress;
  }

  /** Public (no login): confirm the address a token belongs to. */
  async confirmNotificationAddress(token: string): Promise<ConfirmedAddress> {
    const response = await this.instance.post('/notification-addresses/confirm', { token });
    return response.data as ConfirmedAddress;
  }

  /** Universal-intake classification: report order vs enquiry. The
   *  backend is fail-safe (classifier trouble → enquiry/low), so this
   *  only rejects on transport errors. */
  async classifyIntake(params: {
    text: string;
    draftId?: string | null;
    /** The compose page's report-type pick — a classifier HINT
     * (supporting evidence of ordering intent, never decisive). */
    reportConfigId?: string | null;
  }): Promise<IntakeClassification> {
    const response = await this.instance.post(
      '/direct-requests/classify',
      {
        text: params.text,
        draft_id: params.draftId || undefined,
        report_config_id: params.reportConfigId || undefined,
      },
      { timeout: LONG_REQUEST_TIMEOUT_MS }
    );
    return response.data as IntakeClassification;
  }

  /** Create the confirmed request. mode 'direct' → reports created and
   *  screening (reports map mirrors the old extract-multi shape); mode
   *  'triage' → sent to analyst review, only report_request_ids exist. */
  async confirmDirectRequest(body: DirectConfirmRequestBody): Promise<{
    mode: DirectConfirmMode;
    reports: Record<string, Subject[]>;
    reportRequestIds: string[];
  }> {
    const response = await this.instance.post('/direct-requests/confirm', body);
    const reports: Record<string, Subject[]> = {};
    for (const [reportId, subjects] of Object.entries(response.data.reports ?? {})) {
      reports[reportId] = (subjects as ApiSubject[]).map(transformToSubject);
    }
    return {
      mode: response.data.mode as DirectConfirmMode,
      reports,
      reportRequestIds: (response.data.report_request_ids ?? []) as string[],
    };
  }

  /** Async submit for a FILE-BEARING direct request: no client-side
   *  extraction/review — the draft goes straight to triage and files
   *  digest in the background (the analyst retries failures). */
  async submitDirectRequest(body: {
    draft_id: string;
    text?: string;
    report_config_id?: string | null;
    expedited?: boolean;
  }): Promise<{ mode: DirectConfirmMode; reportRequestIds: string[] }> {
    const response = await this.instance.post('/direct-requests/submit', body);
    return {
      mode: response.data.mode as DirectConfirmMode,
      reportRequestIds: (response.data.report_request_ids ?? []) as string[],
    };
  }

  async getSubjectReport(id: string): Promise<{ report: string; run_id?: string }> {
    const response = await this.instance.get(`/subjects/${id}/report`);
    return response.data;
  }

  async escalateSubjects(
    subjects: { id: string; name: string; scope: SubjectScopeType; report_id?: string }[]
  ): Promise<EscalatedSubject[]> {
    const response = await this.instance.post(
      '/escalation/subjects',
      { subjects },
      { timeout: LONG_REQUEST_TIMEOUT_MS }
    );
    const results = response.data.escalation_results || response.data;

    // Transform the response to extract the nested 'response' object from each result
    if (Array.isArray(results) && results.length > 0 && 'response' in results[0]) {
      return results.map((result: any) => result.response);
    }

    return results;
  }

  async checkEscalationStatus(
    threadId: string,
    subject: EscalatedSubject,
    options?: RequestOptions
  ): Promise<EscalationProcessingStatusResponse> {
    const response = await this.instance.post(`/escalation/status/${threadId}`, subject, {
      signal: options?.signal,
    });
    return response.data;
  }

  // Report Blender endpoints
  async startBlend(data: StartBlendRequest): Promise<StartBlendResponse> {
    const response = await this.instance.post('/report-blender/blend', data, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  }

  async getBlendStatus(threadId: string): Promise<BlendStatusResponse> {
    const response = await this.instance.get(`/report-blender/blend/${threadId}/status`);
    return response.data;
  }

  async getLatestBlendForSubject(subjectId: string): Promise<BlendStatusResponse> {
    const response = await this.instance.get(`/report-blender/subjects/${subjectId}/latest-blend`);
    return response.data;
  }

  async getBlendStatusById(
    blendedReportId: string,
    forceRefresh: boolean = false,
    options?: RequestOptions
  ): Promise<BlendStatusResponse> {
    const response = await this.instance.get(`/report-blender/${blendedReportId}/status`, {
      params: { force_refresh: forceRefresh },
      signal: options?.signal,
    });
    return response.data;
  }

  // Google Docs Export endpoints
  async triggerGoogleDocsExport(
    blendedReportId: string,
    request?: TriggerExportRequest
  ): Promise<TriggerExportResponse> {
    const response = await this.instance.post(`/exports/${blendedReportId}`, request || {}, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  }

  async getGoogleDocsExportStatus(
    exportId: string,
    options?: RequestOptions
  ): Promise<GoogleDocsExportResponse> {
    const response = await this.instance.get(`/exports/${exportId}`, { signal: options?.signal });
    return response.data;
  }

  async getGoogleDocsExportByBlendedReport(
    blendedReportId: string
  ): Promise<GoogleDocsExportResponse | null> {
    const response = await this.instance.get(`/exports/by-blended-report/${blendedReportId}`);
    return response.data;
  }

  async retryGoogleDocsExport(exportId: string): Promise<TriggerExportResponse> {
    const response = await this.instance.post(`/exports/${exportId}/retry`, undefined, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return response.data;
  }

  // Reports endpoints
  async listReports(params: {
    page?: number;
    limit?: number;
    /** Combined substring search: subject, requester email, codename, report type. */
    search?: string;
    sort?: string;
    status?: string;
    /** Admin filter: exact team id. */
    team_id?: string;
    /** Screening hit rating filter: hit / potential / clear / unrated. */
    rating?: string;
  }): Promise<{ items: ReportSummary[]; total: number }> {
    const response = await this.instance.get('/reports/', { params });
    return {
      items: (response.data.items as ApiReportSummary[]).map(transformToReportSummary),
      total: response.data.total,
    };
  }

  async getLatestReport(subjectId: string, level?: ReportLevel): Promise<ReportResponse> {
    const params: Record<string, string> = {};
    if (level) {
      params.level = level;
    }
    const response = await this.instance.get(`/reports/subjects/${subjectId}/latest`, { params });
    return response.data;
  }

  async getReport(reportId: string, options?: RequestOptions): Promise<ReportResponse> {
    const response = await this.instance.get(`/reports/${reportId}`, { signal: options?.signal });
    return response.data;
  }

  async getReportStatus(reportId: string, options?: RequestOptions): Promise<ReportStatusResponse> {
    const response = await this.instance.get(`/reports/${reportId}/status`, {
      signal: options?.signal,
    });
    return response.data;
  }

  /**
   * Activate a report sitting in `pending_activation` → enqueues it for
   * processing. Allowed for admins and members of the owning team. The backend
   * returns 404 for a wrong-team caller (don't-leak-existence), and 409 if the
   * report isn't in `pending_activation` (already started). Returns the updated
   * report (same shape as getReport).
   */
  async activateReport(reportId: string): Promise<ReportResponse> {
    const response = await this.instance.post(`/reports/${reportId}/activate`);
    return response.data;
  }

  /**
   * Escalate a report to a different report config for deeper investigation.
   * Re-runs the original report's (already-resolved) subjects under
   * ``report_config_id``, records the escalation link with an optional
   * ``reason``, and enqueues the new report. Returns the link plus the
   * newly-created report (so the caller can navigate to it).
   */
  async escalateReport(
    reportId: string,
    body: ReportEscalationRequest
  ): Promise<ReportEscalationResponse> {
    const response = await this.instance.post(`/reports/${reportId}/escalate`, body, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return response.data as ReportEscalationResponse;
  }

  /**
   * Admin: resume a stuck or failed per-subject remediation run. Creates a
   * new LangGraph run on the subject's existing thread rather than
   * re-triggering from scratch - the platform resumes from the thread's
   * last checkpoint, so this does not redo data acquisition or research.
   * 409s if there's nothing in-flight to resume for this subject, or if
   * remediation is still genuinely running.
   */
  async retrySubjectRemediation(
    reportId: string,
    subjectId: string
  ): Promise<RetryRemediationResponse> {
    const response = await this.instance.post(
      `/reports/${reportId}/subjects/${subjectId}/retry-remediation`
    );
    return response.data as RetryRemediationResponse;
  }

  /**
   * Resolved subjects for a report (name / type / jurisdictions / additional
   * info) — drives the pending-activation subject card. Kept separate from
   * getReport so that payload stays lean.
   */
  async getReportSubjects(reportId: string): Promise<Subject[]> {
    const response = await this.instance.get(`/reports/${reportId}/subjects`);
    return (response.data as ApiSubject[]).map(transformToSubject);
  }

  async getReportVersions(subjectId: string, level?: ReportLevel): Promise<ReportVersionSummary[]> {
    const params: Record<string, string> = {};
    if (level) {
      params.level = level;
    }
    const response = await this.instance.get(`/reports/subjects/${subjectId}/versions`, {
      params,
    });
    return response.data;
  }

  // Subject Identification / Candidates endpoints

  /** Every subject on the report with its active candidates. Polled during
   * identification — conditional: see `ConditionalRequestOptions`. */
  async getReportCandidates(
    reportId: string,
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<SubjectWithCandidates[]>> {
    return this.getConditional<SubjectWithCandidates[]>(
      `/reports/${reportId}/candidates`,
      undefined,
      options
    );
  }

  async getSubjectCandidates(
    reportId: string,
    subjectId: string
  ): Promise<SubjectCandidateResponse[]> {
    const response = await this.instance.get(
      `/reports/${reportId}/subjects/${subjectId}/candidates`
    );
    return response.data;
  }

  async submitSubjectSelection(
    reportId: string,
    subjectId: string,
    selection: SubjectSelection
  ): Promise<SelectionResponse> {
    const response = await this.instance.post(
      `/reports/${reportId}/subjects/${subjectId}/selection`,
      selection
    );
    return response.data;
  }

  // Report Feedback endpoints
  async submitReportFeedback(
    reportId: string,
    feedback: string,
    context?: string,
    anchors?: FeedbackAnchors | null
  ): Promise<ReportFeedbackResponse> {
    const data: ReportFeedbackCreate = { feedback, context, anchors: anchors ?? undefined };
    const response = await this.instance.post(`/reports/${reportId}/feedback`, data);
    return response.data;
  }

  async getReportFeedback(reportId: string): Promise<ReportFeedbackResponse[]> {
    const response = await this.instance.get(`/reports/${reportId}/feedback`);
    return response.data;
  }

  async getReportContentVersions(
    reportId: string,
    options?: RequestOptions
  ): Promise<ReportContentVersionResponse[]> {
    const response = await this.instance.get(`/reports/${reportId}/content`, {
      signal: options?.signal,
    });
    return response.data;
  }

  /**
   * Returns a short-lived presigned S3 URL for the latest .docx version,
   * plus the friendly filename the download should be saved under.
   */
  async downloadReportDocx(reportId: string): Promise<{ url: string; filename: string }> {
    const response = await this.instance.get(`/reports/${reportId}/download-docx`);
    return { url: response.data.url as string, filename: response.data.filename as string };
  }

  /**
   * Returns a short-lived presigned S3 URL for the PDF derivative of the
   * latest .docx version (converted lazily server-side on first request),
   * plus the friendly filename the download should be saved under.
   * 502/503 mean the PDF cannot be produced right now — callers degrade to
   * the Content view.
   */
  async downloadReportPdf(reportId: string): Promise<{ url: string; filename: string }> {
    const response = await this.instance.get(`/reports/${reportId}/download-pdf`, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return { url: response.data.url as string, filename: response.data.filename as string };
  }

  /** Upload a manually edited .docx as a new version of an existing docx report. */
  async uploadDocxVersion(reportId: string, file: File): Promise<DocxIngestResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.instance.post(
      `/reports/${reportId}/upload-docx-version`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPLOAD_TIMEOUT_MS }
    );
    return response.data as DocxIngestResponse;
  }

  // Backoffice file selection endpoints
  /** Every failure and retry attempt on a report, newest first. Admin-only. */
  async getReportFailures(reportId: string): Promise<ReportRunFailure[]> {
    const response = await this.instance.get(`/reports/${reportId}/failures`);
    return response.data as ReportRunFailure[];
  }

  /**
   * Re-fire the graph work that died for one subject. Omit `subjectId` for
   * report-level failures (synthesis, composition), which fail for the
   * report rather than for a subject.
   */
  async retryFailedRun(
    reportId: string,
    subjectId?: string | null
  ): Promise<RetryFailedRunResponse> {
    const response = await this.instance.post(`/reports/${reportId}/retry`, {
      subject_id: subjectId ?? null,
    });
    return response.data as RetryFailedRunResponse;
  }

  async getBackofficeFiles(reportId: string): Promise<BackofficeFile[]> {
    const response = await this.instance.get(`/reports/${reportId}/backoffice-files`);
    return response.data as BackofficeFile[];
  }

  /** Presigned URL for the Gotenberg PDF rendering of a Documents-tab docx. */
  async getBackofficeFilePdfPreview(
    reportId: string,
    rawDataKey: string
  ): Promise<{ url: string }> {
    const response = await this.instance.get(`/reports/${reportId}/backoffice-files/pdf-preview`, {
      params: { raw_data_key: rawDataKey },
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return response.data as { url: string };
  }

  /** Share / unshare an NP-submitted file with the client (admin). */
  async setBackofficeFileVisibility(
    reportId: string,
    rawDataKey: string,
    clientVisible: boolean
  ): Promise<{ raw_data_key: string; client_visible: boolean }> {
    const response = await this.instance.patch(
      `/reports/${reportId}/backoffice-files/client-visibility`,
      { raw_data_key: rawDataKey, client_visible: clientVisible }
    );
    return response.data as { raw_data_key: string; client_visible: boolean };
  }

  /** QA loop: push the current report docx back to the partner's Backoffice
   * assignment for rework (optionally with a message on their NP thread). */
  async sendReportToNp(
    reportId: string,
    body: { assignment_id: string; message?: string | null }
  ): Promise<{ sent: boolean; message_sent: boolean | null }> {
    const response = await this.instance.post(`/reports/${reportId}/send-to-np`, body);
    return response.data as { sent: boolean; message_sent: boolean | null };
  }

  async selectReportFile(reportId: string, rawDataKey: string): Promise<void> {
    await this.instance.post(`/reports/${reportId}/select-report-file`, {
      raw_data_key: rawDataKey,
    });
  }

  // Related entities (admin-only) — adjudication while a report sits at
  // awaiting_scope_confirmation.
  // Adjudication happens in the triage Identification step, so these hang off
  // the report request rather than the report.
  async getRelatedEntities(reportRequestId: string): Promise<RelatedEntitiesResult> {
    const response = await this.instance.get(
      `/report-requests/${reportRequestId}/triage/related-entities`
    );
    return transformToRelatedEntities(response.data as ApiRelatedEntitiesResponse);
  }

  async setRelatedEntityDisposition(
    reportRequestId: string,
    relatedEntityId: string,
    action: RelatedEntityAction
  ): Promise<RelatedEntityDispositionResult> {
    const response = await this.instance.post(
      `/report-requests/${reportRequestId}/triage/related-entities/${relatedEntityId}/disposition`,
      { action }
    );
    return transformToRelatedEntityDisposition(
      response.data as ApiRelatedEntityDispositionResponse
    );
  }

  /**
   * Opt a depth-2 entity into review. Moves it into the subject's main list
   * and, because only surfaced rows are auto-resolved to context at Start,
   * is what lets it reach the pipeline at all.
   */
  async surfaceRelatedEntity(
    reportRequestId: string,
    relatedEntityId: string
  ): Promise<RelatedEntity> {
    const response = await this.instance.post(
      `/report-requests/${reportRequestId}/triage/related-entities/${relatedEntityId}/surface`
    );
    return transformToRelatedEntity(response.data as ApiRelatedEntity);
  }

  // Report Approval endpoints (admin-only)
  async approveReport(reportId: string, notes?: string): Promise<ReportApproval> {
    const response = await this.instance.post(`/reports/${reportId}/approve`, {
      notes: notes ?? null,
    });
    return transformToReportApproval(response.data as ApiReportApproval);
  }

  async revokeReportApproval(reportId: string, notes?: string): Promise<ReportApproval> {
    const response = await this.instance.post(`/reports/${reportId}/revoke`, {
      notes: notes ?? null,
    });
    return transformToReportApproval(response.data as ApiReportApproval);
  }

  async getReportApprovals(reportId: string): Promise<ReportApproval[]> {
    const response = await this.instance.get(`/reports/${reportId}/approvals`);
    return (response.data as ApiReportApproval[]).map(transformToReportApproval);
  }

  // Match review endpoints (admin-only): the analyst gate between
  // remediation and synthesis.
  async getMatchReviews(reportId: string): Promise<MatchReviewList> {
    const response = await this.instance.get(`/reports/${reportId}/match-reviews`);
    return transformToMatchReviewList(response.data as ApiMatchReviewList);
  }

  async decideMatchReview(
    reportId: string,
    reviewId: string,
    decision: MatchReviewDecision,
    note?: string | null
  ): Promise<RemediationReview> {
    const response = await this.instance.post(
      `/reports/${reportId}/match-reviews/${reviewId}/decide`,
      { decision, note: note ?? null }
    );
    return transformToRemediationReview(response.data as ApiRemediationReview);
  }

  /** Stamp the AI's own decision as the verdict on every undecided row. */
  async acceptAiMatchReviews(reportId: string): Promise<MatchReviewList> {
    const response = await this.instance.post(`/reports/${reportId}/match-reviews/accept-ai`);
    return transformToMatchReviewList(response.data as ApiMatchReviewList);
  }

  /** Apply every verdict and let the report continue. 409 while any row
   * is undecided, or when nothing is pending. */
  async submitMatchReview(reportId: string): Promise<MatchReviewSubmitResponse> {
    const response = await this.instance.post(`/reports/${reportId}/match-reviews/submit`);
    return transformToMatchReviewSubmitResponse(response.data as ApiMatchReviewSubmitResponse);
  }

  // Profile review endpoints: the gate before a subject's social media
  // screening is ordered. Same access as reading the report, so they are
  // open to the owning team (every role) as well as admins, not
  // admin-only: the requesting client confirms its own subjects' profiles.
  async getProfileReviews(reportId: string): Promise<ProfileReviewList> {
    const response = await this.instance.get(`/reports/${reportId}/profile-reviews`);
    return transformToProfileReviewList(response.data as ApiProfileReviewList);
  }

  /** Record (or change) the verdict on one pending row. 404 unknown row,
   * 409 when the row is no longer pending. */
  async decideProfileReview(
    reportId: string,
    rowId: string,
    decision: ProfileDecision,
    note?: string | null
  ): Promise<SubjectProfile> {
    const response = await this.instance.post(
      `/reports/${reportId}/profile-reviews/${rowId}/decide`,
      { decision, note: note ?? null }
    );
    return transformToSubjectProfile(response.data as ApiSubjectProfile);
  }

  /** Add a profile the reviewer knows of; the row comes back already
   * confirmed. 400 not a profile URL on a screened platform, 404 subject
   * not on the report, 409 duplicate or subject without a build yet. */
  async addProfileReview(
    reportId: string,
    subjectId: string,
    url: string,
    note?: string | null
  ): Promise<SubjectProfile> {
    const response = await this.instance.post(`/reports/${reportId}/profile-reviews`, {
      subject_id: subjectId,
      url,
      note: note ?? null,
    });
    return transformToSubjectProfile(response.data as ApiSubjectProfile);
  }

  /** Reject every pending row still without a verdict in one call and
   * return the refreshed listing. 409 when nothing is pending. */
  async rejectUndecidedProfileReviews(reportId: string): Promise<ProfileReviewList> {
    const response = await this.instance.post(
      `/reports/${reportId}/profile-reviews/reject-undecided`
    );
    return transformToProfileReviewList(response.data as ApiProfileReviewList);
  }

  /** Release the confirmed profiles to screening and let the report
   * continue. 409 while any row is undecided, or when nothing is pending. */
  async submitProfileReview(reportId: string): Promise<ProfileReviewSubmitResponse> {
    const response = await this.instance.post(`/reports/${reportId}/profile-reviews/submit`);
    return transformToProfileReviewSubmitResponse(response.data as ApiProfileReviewSubmitResponse);
  }

  /** Admin triage: reopen one retained finding for adjudication. Creates an
   * `admin_reopened` match review that the decide → submit flow applies. */
  async reopenScreeningFinding(reportId: string, findingId: string): Promise<RemediationReview> {
    const response = await this.instance.post(
      `/reports/${reportId}/screening-findings/${findingId}/reopen`
    );
    return transformToRemediationReview(response.data as ApiRemediationReview);
  }

  // Report Requests — Phase 02 multi-stage triage
  async listReportRequests(params: {
    page?: number;
    limit?: number;
    triage_stage?: TriageStage;
    team_id?: string | number;
  }): Promise<{ items: ReportRequestSummary[]; total: number }> {
    const response = await this.instance.get('/report-requests', { params });
    return {
      items: (response.data.items as ApiReportRequestSummary[]).map(
        transformToReportRequestSummary
      ),
      total: response.data.total,
    };
  }

  async getReportRequest(id: string): Promise<ReportRequestDetail> {
    const response = await this.instance.get(`/report-requests/${id}`);
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  // Client-facing report requests — the /requests tab. Team-scoped
  // server-side; basic facts only (no triage machinery on the wire).
  async listClientReportRequests(params: {
    page?: number;
    limit?: number;
    /** Case-insensitive substring across codename, subjects, report
     * type and requester email — filtered server-side, pre-pagination. */
    q?: string;
    /** Displayed-status filter (hoisted report stages + closed). */
    status?: ClientRequestStatusFilter;
  }): Promise<{ items: ClientReportRequestSummary[]; total: number }> {
    const response = await this.instance.get('/client/report-requests', {
      params: { ...params, q: params.q || undefined, status: params.status || undefined },
    });
    return {
      items: (response.data.items as ApiClientReportRequestSummary[]).map(
        transformToClientReportRequestSummary
      ),
      total: response.data.total,
    };
  }

  async getClientReportRequest(id: string): Promise<ClientReportRequestSummary> {
    const response = await this.instance.get(`/client/report-requests/${id}`);
    return transformToClientReportRequestSummary(response.data as ApiClientReportRequestSummary);
  }

  /** Client-editable fields on their own request — currently just the
   * client reference. Whitespace-only clears it server-side. */
  async updateClientReportRequest(
    id: string,
    body: { client_reference?: string | null }
  ): Promise<ClientReportRequestSummary> {
    const response = await this.instance.patch(`/client/report-requests/${id}`, body);
    return transformToClientReportRequestSummary(response.data as ApiClientReportRequestSummary);
  }

  // Client enquiries — the portal free-text path. The enquiry's
  // conversation is served by the existing /chat endpoints
  // (entity_type=enquiry).
  async createClientEnquiry(payload: ClientEnquiryCreatePayload): Promise<ClientEnquiry> {
    const response = await this.instance.post('/client/enquiries', {
      text: payload.text,
      subject_hint: payload.subjectHint || undefined,
      draft_id: payload.draftId || undefined,
      team_id: payload.teamId || undefined,
    });
    return transformToClientEnquiry(response.data as ApiClientEnquiry);
  }

  async getClientEnquiry(id: string): Promise<ClientEnquiry> {
    const response = await this.instance.get(`/client/enquiries/${id}`);
    return transformToClientEnquiry(response.data as ApiClientEnquiry);
  }

  // Case sharing — who else on the team can see this request. Managed
  // by client admins (and GTI admins); restricted roles read-only.
  async listRequestParticipants(requestId: string): Promise<CaseParticipant[]> {
    const response = await this.instance.get(`/client/report-requests/${requestId}/participants`);
    return (response.data as ApiCaseParticipant[]).map(transformToCaseParticipant);
  }

  async addRequestParticipant(requestId: string, email: string): Promise<CaseParticipant> {
    const response = await this.instance.post(`/client/report-requests/${requestId}/participants`, {
      email,
    });
    return transformToCaseParticipant(response.data as ApiCaseParticipant);
  }

  async removeRequestParticipant(requestId: string, participantId: string): Promise<void> {
    await this.instance.delete(
      `/client/report-requests/${requestId}/participants/${participantId}`
    );
  }

  /** Client-side members of the request's team — the sharing picker's
   * option pool (backed by the Backoffice membership lookup; 502 when
   * that system is unreachable, callers degrade to free-text entry). */
  async listRequestTeamMembers(requestId: string): Promise<ClientTeamMember[]> {
    const response = await this.instance.get(`/client/report-requests/${requestId}/team-members`);
    return (response.data as ApiClientTeamMember[]).map(transformToClientTeamMember);
  }

  // Report-anchored case sharing — the direct share path for self-served
  // reports (no parent request to anchor on). Same semantics as the
  // request variants above.
  async listReportParticipants(reportId: string): Promise<CaseParticipant[]> {
    const response = await this.instance.get(`/reports/${reportId}/participants`);
    return (response.data as ApiCaseParticipant[]).map(transformToCaseParticipant);
  }

  async addReportParticipant(reportId: string, email: string): Promise<CaseParticipant> {
    const response = await this.instance.post(`/reports/${reportId}/participants`, { email });
    return transformToCaseParticipant(response.data as ApiCaseParticipant);
  }

  async removeReportParticipant(reportId: string, participantId: string): Promise<void> {
    await this.instance.delete(`/reports/${reportId}/participants/${participantId}`);
  }

  async listReportTeamMembers(reportId: string): Promise<ClientTeamMember[]> {
    const response = await this.instance.get(`/reports/${reportId}/team-members`);
    return (response.data as ApiClientTeamMember[]).map(transformToClientTeamMember);
  }

  // Enquiry-anchored case sharing — reaches every request and report
  // raised from the enquiry. 409 while the enquiry has no team.
  async listEnquiryParticipants(enquiryId: string): Promise<CaseParticipant[]> {
    const response = await this.instance.get(`/client/enquiries/${enquiryId}/participants`);
    return (response.data as ApiCaseParticipant[]).map(transformToCaseParticipant);
  }

  async addEnquiryParticipant(enquiryId: string, email: string): Promise<CaseParticipant> {
    const response = await this.instance.post(`/client/enquiries/${enquiryId}/participants`, {
      email,
    });
    return transformToCaseParticipant(response.data as ApiCaseParticipant);
  }

  async removeEnquiryParticipant(enquiryId: string, participantId: string): Promise<void> {
    await this.instance.delete(`/client/enquiries/${enquiryId}/participants/${participantId}`);
  }

  async listEnquiryTeamMembers(enquiryId: string): Promise<ClientTeamMember[]> {
    const response = await this.instance.get(`/client/enquiries/${enquiryId}/team-members`);
    return (response.data as ApiClientTeamMember[]).map(transformToClientTeamMember);
  }

  /** The codename family's member chain — powers the /cases page. */
  async getCase(familyKey: string, options?: RequestOptions): Promise<CaseDetail> {
    const response = await this.instance.get(`/cases/${encodeURIComponent(familyKey)}`, {
      signal: options?.signal,
    });
    return transformToCaseDetail(response.data as ApiCaseDetail);
  }

  /** Who did what on the case, newest first — the Inbox preview's
   * activity feed (triage moves, messages, approvals, partner events,
   * resolved queue items). */
  async getCaseActivity(familyKey: string, options?: RequestOptions): Promise<CaseActivity> {
    const response = await this.instance.get(`/cases/${encodeURIComponent(familyKey)}/activity`, {
      signal: options?.signal,
    });
    return transformToCaseActivity(response.data as ApiCaseActivity);
  }

  /** Set (or clear, with `userId: null`) the case's owning analyst —
   * written family-wide across the enquiry and its report requests. */
  async claimCase(
    familyKey: string,
    userId: string | null
  ): Promise<{
    assignedAnalystUserId: string | null;
    updatedEntities: number;
    /** Backoffice projects now carrying the new CX lead / that could not be
     * updated (recorded on that report; the claim itself is kept). */
    backofficeProjectsUpdated: number;
    backofficeProjectsFailed: number;
  }> {
    const response = await this.instance.post(`/cases/${encodeURIComponent(familyKey)}/claim`, {
      user_id: userId,
    });
    const data = response.data as {
      assigned_analyst_user_id: string | null;
      updated_entities: number;
      backoffice_projects_updated?: number;
      backoffice_projects_failed?: number;
    };
    return {
      assignedAnalystUserId: data.assigned_analyst_user_id,
      updatedEntities: data.updated_entities,
      backofficeProjectsUpdated: data.backoffice_projects_updated ?? 0,
      backofficeProjectsFailed: data.backoffice_projects_failed ?? 0,
    };
  }

  /** Entity → family key (the redirect shim for old detail routes). */
  async resolveCase(entityType: string, entityId: string): Promise<string> {
    const response = await this.instance.get(
      `/cases/resolve?entity_type=${entityType}&entity_id=${entityId}`
    );
    return (response.data as { family_key: string }).family_key;
  }

  /** Read-only exit-condition evaluation for the current triage stage —
   * powers the workspace gate strip's blocker chips. */
  async getTriageGates(id: string, options?: RequestOptions): Promise<TriageGatesResponse> {
    const response = await this.instance.get(`/report-requests/${id}/gates`, {
      signal: options?.signal,
    });
    return response.data as TriageGatesResponse;
  }

  /**
   * A list read with version-token polling (`docs/list-endpoints.md` in
   * vNext-api). Sends `If-None-Match` when `options.etag` is set and turns
   * the steady-state 304 into `'not-modified'` instead of an axios error;
   * a 200 comes back with its `ETag` so the caller can poll on it.
   */
  private async getConditional<T>(
    url: string,
    params: Record<string, string | number | boolean | undefined> | undefined,
    options?: ConditionalRequestOptions,
    transform: (raw: unknown) => T = (raw) => raw as T
  ): Promise<ConditionalResult<T>> {
    const response = await this.instance.get(url, {
      params,
      signal: options?.signal,
      headers: options?.etag ? { 'If-None-Match': options.etag } : undefined,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
    });
    if (response.status === 304) {
      return 'not-modified';
    }
    return {
      data: transform(response.data),
      etag: (response.headers?.etag as string | undefined) ?? null,
    };
  }

  // Inbox — Phase 03 / 06 / 08 surface. The full Inbox UI (Phase 08)
  // uses every filter; the Phase-06 revert banner uses
  // `entity_id` + `kind`. Polling-driven live updates run on top of
  // `listInbox` / `listInboxGrouped` (see Plan 08's live-updates
  // decision: polling for v1, ~5s interval).
  /** Flat inbox list. Conditional: see `ConditionalRequestOptions`. */
  async listInbox(
    params: InboxListParams,
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<InboxListResponse>> {
    const apiParams: Record<string, string | number | boolean | undefined> = {
      state: params.state,
      kind: params.kind,
      priority: params.priority,
      entity_type: params.entityType,
      entity_id: params.entityId,
      assigned_to: params.assignedTo,
      team_id: params.teamId,
      include_team_null: params.includeTeamNull,
      page: params.page,
      limit: params.limit,
    };
    return this.getConditional('/inbox', apiParams, options, (raw) => {
      const data = raw as { items: Parameters<typeof transformToInboxItem>[0][]; total: number };
      return { items: data.items.map(transformToInboxItem), total: data.total };
    });
  }

  /** The Browse tab: every Enquiry / ReportRequest / Report as one
   * searchable, paginated set. `mode=cases` pages codename families and
   * returns every member of the page's families. */
  async browseEntities(
    params: InboxBrowseParams,
    options?: RequestOptions
  ): Promise<ApiInboxBrowseResponse> {
    const response = await this.instance.get('/inbox/browse', {
      params: {
        q: params.q || undefined,
        team_id: params.teamId || undefined,
        kinds: params.kinds?.length ? params.kinds.join(',') : undefined,
        mode: params.mode,
        page: params.page,
        page_size: params.pageSize,
      },
      signal: options?.signal,
    });
    return response.data as ApiInboxBrowseResponse;
  }

  /**
   * Grouped inbox read with ETag-aware polling.
   *
   * Pass the ETag from a previous response via `options.etag` and the
   * request carries `If-None-Match`; an unchanged set answers 304 with
   * no body, which this method surfaces as the literal `'not-modified'`
   * so pollers can skip the state update. Omit `options.etag` to force
   * a full fetch.
   */
  async listInboxGrouped(
    params: InboxGroupedParams,
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<InboxGroupedResponse>> {
    const apiParams: Record<string, string | number | boolean | undefined> = {
      state: params.state,
      kind: params.kind,
      priority: params.priority,
      assigned_to: params.assignedTo,
      team_id: params.teamId,
      // No-team items are always surfaced in the inbox; the old opt-in toggle
      // was removed. Sent explicitly so we don't depend on the backend default.
      include_team_null: params.includeTeamNull ?? true,
      q: params.search,
      page: params.page,
      page_size: params.pageSize,
    };
    return this.getConditional('/inbox/grouped', apiParams, options, (raw) =>
      transformToInboxGrouped(raw as Parameters<typeof transformToInboxGrouped>[0])
    );
  }

  /** Full inbox-item detail — the grouped list serves a slim shape. */
  async getInboxItem(itemId: string): Promise<InboxItem> {
    const response = await this.instance.get(`/inbox/${itemId}`);
    return transformToInboxItem(response.data as Parameters<typeof transformToInboxItem>[0]);
  }

  /**
   * Rename a codename family. 409 = name taken, 404 = stale family key
   * (renamed under our feet — refetch and retry); both are surfaced as
   * axios errors for the caller to map.
   */
  async renameCodename(familyKey: string, newCodename: string): Promise<CodenameRenameResult> {
    const response = await this.instance.patch(`/codenames/${encodeURIComponent(familyKey)}`, {
      new_codename: newCodename,
    });
    return transformToCodenameRenameResult(
      response.data as Parameters<typeof transformToCodenameRenameResult>[0]
    );
  }

  async listInboxKinds(): Promise<string[]> {
    const response = await this.instance.get('/inbox/kinds');
    return (response.data.kinds as string[]) ?? [];
  }

  /** Open needs-you count — the top-nav Inbox badge. */
  async getInboxCount(options?: RequestOptions): Promise<number> {
    const response = await this.instance.get('/inbox/count', { signal: options?.signal });
    return (response.data.needs_you as number) ?? 0;
  }

  async snoozeInbox(itemId: string, body: InboxSnoozeRequest): Promise<InboxItem> {
    const response = await this.instance.post(`/inbox/${itemId}/snooze`, {
      wake_at: body.wakeAt,
      reason: body.reason ?? null,
    });
    return transformToInboxItem(response.data as Parameters<typeof transformToInboxItem>[0]);
  }

  async dismissInbox(itemId: string, body: InboxDismissRequest): Promise<InboxItem> {
    const response = await this.instance.post(`/inbox/${itemId}/dismiss`, {
      reason: body.reason,
    });
    return transformToInboxItem(response.data as Parameters<typeof transformToInboxItem>[0]);
  }

  async assignInbox(itemId: string, body: InboxAssignRequest): Promise<InboxItem> {
    const response = await this.instance.post(`/inbox/${itemId}/assign`, {
      user_id: body.userId,
    });
    return transformToInboxItem(response.data as Parameters<typeof transformToInboxItem>[0]);
  }

  async resolveInbox(itemId: string, body: InboxResolveRequest): Promise<InboxItem> {
    const response = await this.instance.post(`/inbox/${itemId}/resolve`, {
      notes: body.notes ?? null,
    });
    return transformToInboxItem(response.data as Parameters<typeof transformToInboxItem>[0]);
  }

  async markInboxSeen(ids: string[]): Promise<number> {
    if (ids.length === 0) {
      return 0;
    }
    const response = await this.instance.post('/inbox/seen', { ids });
    return (response.data.updated as number) ?? 0;
  }

  /** The Cases dashboard — ops parity with the Backoffice sheet. One
   * row per case with NP assignment sub-rows. Conditional: see
   * `ConditionalRequestOptions`. */
  async getCaseDashboard(
    view: DashboardView,
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<CaseDashboardResponse>> {
    return this.getConditional('/inbox/dashboard', { view }, options, (raw) =>
      transformToCaseDashboard(raw as ApiCaseDashboardResponse)
    );
  }

  /** Set (or clear, with null / blank) a case's analyst note. */
  async updateDashboardNotes(
    reportRequestId: string,
    notes: string | null
  ): Promise<string | null> {
    const response = await this.instance.put(`/inbox/dashboard/${reportRequestId}/notes`, {
      notes,
    });
    return (response.data as { analyst_notes: string | null }).analyst_notes;
  }

  /** Set (or clear, with null) the report's 1st / 2nd QA reviewer.
   * Admin-only; PATCH semantics — omitted slots stay untouched. */
  async setReportQaReviewers(
    reportId: string,
    updates: { firstQaUserId?: string | null; secondQaUserId?: string | null }
  ): Promise<void> {
    const body: Record<string, string | null> = {};
    if ('firstQaUserId' in updates) {
      body.first_qa_user_id = updates.firstQaUserId ?? null;
    }
    if ('secondQaUserId' in updates) {
      body.second_qa_user_id = updates.secondQaUserId ?? null;
    }
    await this.instance.patch(`/reports/${reportId}/qa-reviewers`, body);
  }

  async updateTriageInitial(
    id: string,
    body: TriageInitialUpdateRequest
  ): Promise<ReportRequestDetail> {
    const response = await this.instance.patch(`/report-requests/${id}/triage/initial`, body);
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  /** England bank-holiday dates ("YYYY-MM-DD") — the calendar behind
   * the triage TAT ↔ due-date coupling; one source shared with the
   * backend's business-day maths. */
  async getUkBankHolidays(): Promise<string[]> {
    const response = await this.instance.get('/meta/uk-bank-holidays');
    return (response.data as { holidays: string[] }).holidays;
  }

  /** Explicit pick of which attached files feed subject extraction
   * ([] = typed lines only; null = back to automatic provenance
   * scoping). INITIAL-stage only — 409 later. */
  async updateExtractionSources(
    id: string,
    attachmentIds: string[] | null
  ): Promise<ReportRequestDetail> {
    const response = await this.instance.put(`/report-requests/${id}/extraction-sources`, {
      attachment_ids: attachmentIds,
    });
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  /** Pre-plan config pick (INITIAL → SUBJECTS_IDENTIFIED) — settles a
   *  config the extraction couldn't derive, without a revert. */
  async updateReportRequestConfig(id: string, configId: string): Promise<ReportRequestDetail> {
    const response = await this.instance.patch(`/report-requests/${id}/config`, {
      report_config_id: configId,
    });
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  // Late-stage pricing edits — editable at every stage except started/rejected
  // (those 409). Distinct from /triage/initial so price/turnaround/expedited
  // stay editable through the lifecycle.
  async updatePricing(id: string, body: PricingUpdateRequest): Promise<ReportRequestDetail> {
    const response = await this.instance.patch(`/report-requests/${id}/pricing`, body);
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  async advanceTriage(id: string, body: TriageAdvanceRequest): Promise<ReportRequestDetail> {
    const response = await this.instance.post(`/report-requests/${id}/triage/advance`, body);
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  async revertTriage(id: string, body: TriageRevertRequest): Promise<ReportRequestDetail> {
    const response = await this.instance.post(`/report-requests/${id}/triage/revert`, body);
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  async rejectReportRequest(id: string, body: TriageRejectRequest): Promise<ReportRequestDetail> {
    const response = await this.instance.post(`/report-requests/${id}/reject`, body);
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  async splitReportRequest(id: string): Promise<ApiTriageSplitResponse> {
    const response = await this.instance.post(`/report-requests/${id}/split`);
    return response.data as ApiTriageSplitResponse;
  }

  async startReportRequest(id: string): Promise<TriageStartResponse> {
    const response = await this.instance.post(`/report-requests/${id}/start`);
    return response.data as TriageStartResponse;
  }

  async listTriageSubjects(rrId: string): Promise<ApiSubject[]> {
    const response = await this.instance.get(`/report-requests/${rrId}/triage/subjects`);
    return response.data as ApiSubject[];
  }

  // Triage SUBJECTS_EXTRACTED panel: add a Subject by hand.
  async addTriageSubject(rrId: string, body: TriageAddSubjectRequest): Promise<ApiSubject> {
    const response = await this.instance.post(`/report-requests/${rrId}/triage/subjects`, body);
    return response.data as ApiSubject;
  }

  // Triage SUBJECTS_EXTRACTED panel: edit / remove / re-extract a Subject.
  async editTriageSubject(
    rrId: string,
    subjectId: string,
    body: TriageEditSubjectRequest
  ): Promise<ApiSubject> {
    const response = await this.instance.patch(
      `/report-requests/${rrId}/triage/subjects/${subjectId}`,
      body
    );
    return response.data as ApiSubject;
  }

  async removeTriageSubject(rrId: string, subjectId: string): Promise<void> {
    await this.instance.delete(`/report-requests/${rrId}/triage/subjects/${subjectId}`);
  }

  async reExtractTriageSubject(
    rrId: string,
    subjectId: string,
    additionalContext?: string
  ): Promise<ApiSubject> {
    const response = await this.instance.post(
      `/report-requests/${rrId}/triage/subjects/${subjectId}/re-extract`,
      additionalContext ? { additional_context: additionalContext } : undefined
    );
    return response.data as ApiSubject;
  }

  // Triage SUBJECTS_IDENTIFIED panel: pick a candidate / clarify.
  async selectTriageCandidate(
    rrId: string,
    subjectId: string,
    candidateId: string
  ): Promise<ReportRequestDetail> {
    const response = await this.instance.post(
      `/report-requests/${rrId}/triage/subjects/${subjectId}/select-candidate`,
      { candidate_id: candidateId }
    );
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  async clarifyTriageSubject(
    rrId: string,
    subjectId: string,
    additionalContext: string
  ): Promise<ReportRequestDetail> {
    const response = await this.instance.post(
      `/report-requests/${rrId}/triage/subjects/${subjectId}/clarify`,
      { additional_context: additionalContext }
    );
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  // Triage SUBJECTS_IDENTIFIED panel: resolve a subject manually when the
  // identification graph found nothing to pick. The backend synthesises a
  // selected candidate from the analyst's record so the subject comes back
  // resolved. Allowed only at the SUBJECTS_IDENTIFIED stage (409 otherwise).
  async resolveTriageSubjectManually(
    rrId: string,
    subjectId: string,
    body: TriageManualResolveRequest
  ): Promise<ReportRequestDetail> {
    const response = await this.instance.post(
      `/report-requests/${rrId}/triage/subjects/${subjectId}/manual`,
      body
    );
    return transformToReportRequestDetail(response.data as ApiReportRequestDetail);
  }

  // Enquiries (inbound communications above ReportRequest)

  async listEnquiries(params: {
    page?: number;
    limit?: number;
    stage?: EnquiryStage;
    team_id?: string | null;
    resolution_category?: EnquiryResolution;
  }): Promise<{ items: EnquirySummary[]; total: number }> {
    const response = await this.instance.get('/enquiries', { params });
    return {
      items: (response.data.items as ApiEnquirySummary[]).map(transformToEnquirySummary),
      total: response.data.total,
    };
  }

  async getEnquiry(id: string): Promise<EnquiryDetail> {
    const response = await this.instance.get(`/enquiries/${id}`);
    return transformToEnquiryDetail(response.data as ApiEnquiryDetail);
  }

  async resolveEnquiry(id: string, body: EnquiryResolveRequest): Promise<EnquiryDetail> {
    const response = await this.instance.post(`/enquiries/${id}/resolve`, body);
    return transformToEnquiryDetail(response.data as ApiEnquiryDetail);
  }

  async updateEnquiry(id: string, body: { team_id: string | null }): Promise<EnquiryDetail> {
    const response = await this.instance.patch(`/enquiries/${id}`, body);
    return transformToEnquiryDetail(response.data as ApiEnquiryDetail);
  }

  /** Set an enquiry's team from the person who raised it, or suggest one. Admin-only.
   * An exact Backoffice match is applied; a domain match is only offered. */
  async resolveEnquiryTeam(id: string): Promise<EnquiryTeamResolution> {
    const response = await this.instance.post(`/enquiries/${id}/resolve-team`);
    return transformToEnquiryTeamResolution(response.data as ApiEnquiryTeamResolution);
  }

  async bulkApproveReportsForConfig(
    configId: string,
    teamId?: string | number
  ): Promise<{ approved: number }> {
    const response = await this.instance.post(
      `/report-configs/${configId}/bulk-approve-reports`,
      null,
      { params: teamId ? { team_id: teamId } : undefined }
    );
    return response.data;
  }

  // Report Config endpoints

  /** Picker mode of GET /report-configs/: the whole applicable catalogue. */
  async getReportConfigs(params: {
    team_id?: string | number;
    is_active?: boolean;
  }): Promise<ReportConfigSummary[]> {
    const response = await this.instance.get('/report-configs/', { params });
    return response.data.map((item: ApiReportConfigSummary) =>
      transformToReportConfigSummary(item)
    );
  }

  /** Grid mode of GET /report-configs/: one page, `q` searched server-side
   * over name / slug / description. Same endpoint as `getReportConfigs`;
   * the page params select the mode. */
  async getReportConfigsPage(params: {
    team_id?: string | number;
    is_active?: boolean;
    q?: string;
    page: number;
    page_size?: number;
  }): Promise<{ items: ReportConfigSummary[]; total: number; version: string | null }> {
    const response = await this.instance.get('/report-configs/', { params });
    const data = response.data as {
      items: ApiReportConfigSummary[];
      total: number;
      version?: string | null;
    };
    return {
      items: data.items.map((item) => transformToReportConfigSummary(item)),
      total: data.total,
      version: data.version ?? null,
    };
  }

  async getReportConfig(configId: string, teamId?: string | number): Promise<ReportConfig> {
    const response = await this.instance.get(`/report-configs/${configId}`, {
      params: teamId ? { team_id: teamId } : undefined,
    });
    return transformToReportConfig(response.data as ApiReportConfig);
  }

  // Backoffice template listing (proxied through vNext-api → Django). Feeds the
  // legacy-flow config editor's template Select and the triage tile source.
  async getBackofficeTemplates(teamId?: string | number): Promise<BackofficeTemplate[]> {
    const response = await this.instance.get('/report-configs/backoffice-templates', {
      params: teamId ? { team_id: teamId } : undefined,
    });
    return (response.data as ApiBackofficeTemplate[]).map(transformToBackofficeTemplate);
  }

  async getBackofficeTemplate(
    templateId: number,
    teamId?: string | number
  ): Promise<BackofficeTemplate> {
    const response = await this.instance.get(`/report-configs/backoffice-templates/${templateId}`, {
      params: teamId ? { team_id: teamId } : undefined,
    });
    return transformToBackofficeTemplate(response.data as ApiBackofficeTemplate);
  }

  async createReportConfig(data: ReportConfigCreateRequest): Promise<ReportConfig> {
    const response = await this.instance.post('/report-configs/', data);
    return transformToReportConfig(response.data as ApiReportConfig);
  }

  async updateReportConfig(
    configId: string,
    data: ReportConfigUpdateRequest,
    teamId?: string | number
  ): Promise<ReportConfig> {
    const response = await this.instance.patch(`/report-configs/${configId}`, data, {
      params: teamId ? { team_id: teamId } : undefined,
    });
    return transformToReportConfig(response.data as ApiReportConfig);
  }

  // --- Docx fill templates (plan 15 / F1+F2) ---

  /** Upload the styled docx template a docx-output config fills. */
  async uploadTemplateDocx(configId: string, file: File): Promise<TemplateDocxUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.instance.post(
      `/report-configs/${configId}/template-docx`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: UPLOAD_TIMEOUT_MS }
    );
    return response.data as TemplateDocxUploadResponse;
  }

  /** Parsed region tree of the config's stored template (mapping UI). */
  async getTemplateRegions(configId: string): Promise<RegionTree> {
    const response = await this.instance.get(`/report-configs/${configId}/template/regions`);
    return response.data as RegionTree;
  }

  /** Everything bindable for this config (mapping-UI catalog). */
  async getBindingCatalog(configId: string): Promise<BindingCatalog> {
    const response = await this.instance.get(`/report-configs/${configId}/binding-catalog`);
    return response.data as BindingCatalog;
  }

  /** Inject gtibind_* anchors for designated regions; returns names + refreshed tree. */
  async injectTemplateAnchors(
    configId: string,
    anchors: TemplateAnchorRequest[]
  ): Promise<TemplateAnchorsResponse> {
    const response = await this.instance.post(
      `/report-configs/${configId}/template-anchors`,
      { anchors },
      { timeout: LONG_REQUEST_TIMEOUT_MS }
    );
    return response.data as TemplateAnchorsResponse;
  }

  /** Run the six bind-time validation rules against a draft (or the stored map). */
  async validateBindings(
    configId: string,
    bindingMap?: BindingMap
  ): Promise<BindingValidationReport> {
    const response = await this.instance.post(
      `/report-configs/${configId}/validate-bindings`,
      bindingMap ? { binding_map: bindingMap } : {}
    );
    return response.data as BindingValidationReport;
  }

  async deleteReportConfig(configId: string, teamId?: string | number): Promise<void> {
    await this.instance.delete(`/report-configs/${configId}`, {
      params: teamId ? { team_id: teamId } : undefined,
    });
  }

  async duplicateReportConfig(
    configId: string,
    newName: string,
    newSlug: string,
    teamId?: string | number
  ): Promise<ReportConfig> {
    const response = await this.instance.post(`/report-configs/${configId}/duplicate`, null, {
      params: { new_name: newName, new_slug: newSlug, ...(teamId ? { team_id: teamId } : {}) },
    });
    return transformToReportConfig(response.data as ApiReportConfig);
  }

  async exportReportConfig(
    configId: string,
    teamId?: string | number
  ): Promise<{ payload: ConfigExportPayload; filename: string | null }> {
    const response = await this.instance.get(`/report-configs/${configId}/export`, {
      params: teamId ? { team_id: teamId } : undefined,
    });
    return {
      payload: response.data as ConfigExportPayload,
      filename: parseContentDispositionFilename(
        response.headers?.['content-disposition'] as string | undefined
      ),
    };
  }

  async importReportConfig(
    payload: ConfigExportPayload,
    opts: { teamId?: string | number; onConflict?: ImportOnConflict } = {}
  ): Promise<ReportConfig> {
    const params: Record<string, string | number> = {};
    if (opts.teamId !== undefined) {
      params.team_id = opts.teamId;
    }
    if (opts.onConflict) {
      params.on_conflict = opts.onConflict;
    }

    // Bypass the response interceptor so we can read structured 400
    // detail bodies (missing scope components) without losing them to
    // string coercion in the generic error path.
    const response = await this.instance.post('/report-configs/import', payload, {
      params,
      validateStatus: () => true,
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });

    if (response.status === 201) {
      return transformToReportConfig(response.data as ApiReportConfig);
    }

    const data = response.data as
      | { error?: string; detail?: string | Record<string, unknown> }
      | undefined;
    const detail = data?.detail;

    if (response.status === 409 && detail && typeof detail === 'object') {
      throw new ApiConflictError((detail.message as string) ?? 'Conflict', detail);
    }
    if (response.status === 400 && detail && typeof detail === 'object') {
      const missing = (detail as Partial<ImportMissingScopeComponentsDetail>)
        .missing_scope_component_slugs;
      if (Array.isArray(missing)) {
        throw new ApiValidationError((detail.message as string) ?? 'Validation failed', detail);
      }
    }
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }

    const fallback =
      data?.error ??
      (typeof detail === 'string' ? detail : null) ??
      (detail && typeof detail === 'object' && typeof detail.message === 'string'
        ? detail.message
        : null) ??
      `Error ${response.status}`;
    throw new Error(fallback);
  }

  async setConfigComponents(
    configId: string,
    components: ReportConfigComponentBase[],
    teamId?: string | number
  ): Promise<ReportConfig> {
    const response = await this.instance.put(`/report-configs/${configId}/components`, components, {
      params: teamId ? { team_id: teamId } : undefined,
    });
    return transformToReportConfig(response.data as ApiReportConfig);
  }

  async updateConfigComponent(
    configId: string,
    scopeComponentId: string,
    data: ReportConfigComponentBase,
    teamId?: string | number
  ): Promise<ReportConfig> {
    const response = await this.instance.patch(
      `/report-configs/${configId}/components/${scopeComponentId}`,
      data,
      { params: teamId ? { team_id: teamId } : undefined }
    );
    return transformToReportConfig(response.data as ApiReportConfig);
  }

  // Template-editor endpoints (per-config)
  async getTemplateContextSchema(
    configId: string,
    teamId?: string | number
  ): Promise<TemplateContextSchema> {
    const response = await this.instance.get(
      `/report-configs/${configId}/template/context-schema`,
      { params: teamId ? { team_id: teamId } : undefined }
    );
    return response.data as TemplateContextSchema;
  }

  async validateTemplate(
    configId: string,
    template: string,
    teamId?: string | number
  ): Promise<TemplateValidateResponse> {
    const response = await this.instance.post(
      `/report-configs/${configId}/template/validate`,
      { template } satisfies TemplateValidateRequest,
      { params: teamId ? { team_id: teamId } : undefined }
    );
    return response.data as TemplateValidateResponse;
  }

  async previewTemplate(
    configId: string,
    body: { template?: string | null },
    teamId?: string | number
  ): Promise<TemplatePreviewResponse> {
    const response = await this.instance.post(
      `/report-configs/${configId}/template/preview`,
      body,
      { params: teamId ? { team_id: teamId } : undefined }
    );
    return response.data as TemplatePreviewResponse;
  }

  // Template-editor endpoints (draft mode — no config yet)
  async getDraftTemplateContextSchema(scopeComponentIds: string[]): Promise<TemplateContextSchema> {
    const response = await this.instance.get('/report-configs/template/context-schema', {
      params: scopeComponentIds.length
        ? { scope_component_ids: scopeComponentIds.join(',') }
        : undefined,
    });
    return response.data as TemplateContextSchema;
  }

  async validateDraftTemplate(template: string): Promise<TemplateValidateResponse> {
    const response = await this.instance.post(
      '/report-configs/template/validate',
      { template } satisfies TemplateValidateRequest,
      { timeout: LONG_REQUEST_TIMEOUT_MS }
    );
    return response.data as TemplateValidateResponse;
  }

  async previewDraftTemplate(body: TemplatePreviewRequest): Promise<TemplatePreviewResponse> {
    const response = await this.instance.post('/report-configs/template/preview', body, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return response.data as TemplatePreviewResponse;
  }

  // Scope Component endpoints
  async getScopeComponents({ all = false } = {}): Promise<ScopeComponent[]> {
    const params = all ? { all: true } : {};
    const response = await this.instance.get('/scope-components/', { params });
    return response.data.map((item: ApiScopeComponent) => transformToScopeComponent(item));
  }

  async getScopeComponent(componentId: string): Promise<ScopeComponent> {
    const response = await this.instance.get(`/scope-components/${componentId}`);
    return transformToScopeComponent(response.data as ApiScopeComponent);
  }

  async createScopeComponent(data: ScopeComponentCreateRequest): Promise<ScopeComponent> {
    const response = await this.instance.post('/scope-components/', data);
    return transformToScopeComponent(response.data as ApiScopeComponent);
  }

  async updateScopeComponent(
    componentId: string,
    data: ScopeComponentUpdateRequest
  ): Promise<ScopeComponent> {
    const response = await this.instance.patch(`/scope-components/${componentId}`, data);
    return transformToScopeComponent(response.data as ApiScopeComponent);
  }

  async deleteScopeComponent(componentId: string): Promise<void> {
    await this.instance.delete(`/scope-components/${componentId}`);
  }

  async validateScopeComponentDependencies(
    data: DependencyValidationRequest
  ): Promise<DependencyValidationResponse> {
    const response = await this.instance.post('/scope-components/validate-dependencies', data);
    return response.data;
  }

  async getRenderingContext(): Promise<RenderingContextCatalogue> {
    const response = await this.instance.get('/scope-components/rendering-context');
    return response.data;
  }

  async getDataRequirementEnums(): Promise<DataRequirementEnums> {
    const response = await this.instance.get('/scope-components/data-requirement-enums');
    return response.data;
  }

  async listScopeComponentCapabilities(componentId: string): Promise<ScopeComponentCapability[]> {
    const response = await this.instance.get(`/scope-components/${componentId}/capabilities`);
    return response.data.map((item: ApiScopeComponentCapability) =>
      transformToScopeComponentCapability(item)
    );
  }

  async createScopeComponentCapability(
    componentId: string,
    data: ScopeComponentCapabilityCreateRequest
  ): Promise<ScopeComponentCapability> {
    const response = await this.instance.post(
      `/scope-components/${componentId}/capabilities`,
      data
    );
    return transformToScopeComponentCapability(response.data as ApiScopeComponentCapability);
  }

  async updateScopeComponentCapability(
    componentId: string,
    capabilityId: string,
    data: ScopeComponentCapabilityUpdateRequest
  ): Promise<ScopeComponentCapability> {
    const response = await this.instance.patch(
      `/scope-components/${componentId}/capabilities/${capabilityId}`,
      data
    );
    return transformToScopeComponentCapability(response.data as ApiScopeComponentCapability);
  }

  async deleteScopeComponentCapability(componentId: string, capabilityId: string): Promise<void> {
    await this.instance.delete(`/scope-components/${componentId}/capabilities/${capabilityId}`);
  }

  // ---------------------------------------------------------------
  // Network Partners — admin CRUD (Hub is the source of truth)
  // ---------------------------------------------------------------

  async listNetworkPartners(
    filters: import('@/types/network-partner').NetworkPartnerListFilters = {}
  ): Promise<import('@/types/network-partner').PaginatedNetworkPartners> {
    const params: Record<string, unknown> = {
      status: filters.statuses?.length ? filters.statuses.join(',') : undefined,
      scope: filters.scope || undefined,
      jurisdiction: filters.jurisdiction || undefined,
      language: filters.language || undefined,
      search: filters.search || undefined,
      needs_attention: filters.needsAttention || undefined,
      page: filters.page,
      page_size: filters.pageSize,
    };
    const response = await this.instance.get('/admin/network-partners', { params });
    return response.data as import('@/types/network-partner').PaginatedNetworkPartners;
  }

  /** Triage shortlist search — active + prospect partners only. */
  async searchNetworkPartners(opts: {
    q?: string;
    limit?: number;
  }): Promise<import('@/types/network-partner').NetworkPartnerListItem[]> {
    const result = await this.listNetworkPartners({
      statuses: ['active', 'prospect'],
      search: opts.q,
      pageSize: opts.limit ?? 25,
    });
    return result.items;
  }

  async getNetworkPartner(
    partnerId: string
  ): Promise<import('@/types/network-partner').NetworkPartnerDetail> {
    const response = await this.instance.get(`/admin/network-partners/${partnerId}`);
    return response.data as import('@/types/network-partner').NetworkPartnerDetail;
  }

  async createNetworkPartner(
    payload: import('@/types/network-partner').NetworkPartnerPayload
  ): Promise<import('@/types/network-partner').NetworkPartnerDetail> {
    const response = await this.instance.post('/admin/network-partners', payload);
    return response.data as import('@/types/network-partner').NetworkPartnerDetail;
  }

  async updateNetworkPartner(
    partnerId: string,
    patch: import('@/types/network-partner').NetworkPartnerPayload
  ): Promise<import('@/types/network-partner').NetworkPartnerDetail> {
    const response = await this.instance.patch(`/admin/network-partners/${partnerId}`, patch);
    return response.data as import('@/types/network-partner').NetworkPartnerDetail;
  }

  async archiveNetworkPartner(
    partnerId: string
  ): Promise<import('@/types/network-partner').NetworkPartnerDetail> {
    const response = await this.instance.post(`/admin/network-partners/${partnerId}/archive`);
    return response.data as import('@/types/network-partner').NetworkPartnerDetail;
  }

  async restoreNetworkPartner(
    partnerId: string
  ): Promise<import('@/types/network-partner').NetworkPartnerDetail> {
    const response = await this.instance.post(`/admin/network-partners/${partnerId}/restore`);
    return response.data as import('@/types/network-partner').NetworkPartnerDetail;
  }

  /** Irreversible — there is no unban endpoint. The reason is recorded as
   * a `ban` note on the partner. */
  async banNetworkPartner(
    partnerId: string,
    payload: import('@/types/network-partner').NetworkPartnerBanPayload
  ): Promise<import('@/types/network-partner').NetworkPartnerDetail> {
    const response = await this.instance.post(`/admin/network-partners/${partnerId}/ban`, payload);
    return response.data as import('@/types/network-partner').NetworkPartnerDetail;
  }

  /** One page of the partner's notes stack (stamped notes + reviews,
   * newest first). */
  /** One page of the partner's notes stack — searched (`q`), filtered
   * (`kinds`, rating bounds on `dimension`) and sorted server-side. */
  async listNetworkPartnerNotes(
    partnerId: string,
    query: import('@/types/network-partner').NpNotesQuery = {}
  ): Promise<import('@/types/network-partner').NpNotesResponse> {
    const response = await this.instance.get(`/admin/network-partners/${partnerId}/notes`, {
      params: {
        q: query.q?.trim() || undefined,
        kind: query.kinds?.length ? query.kinds.join(',') : undefined,
        sort: query.sort,
        dimension: query.dimension,
        min_rating: query.minRating,
        max_rating: query.maxRating,
        page: query.page,
        page_size: query.pageSize,
      },
    });
    return response.data as import('@/types/network-partner').NpNotesResponse;
  }

  async addNetworkPartnerNote(
    partnerId: string,
    payload: import('@/types/network-partner').NpNotePayload
  ): Promise<import('@/types/network-partner').NpNote> {
    const response = await this.instance.post(
      `/admin/network-partners/${partnerId}/notes`,
      payload
    );
    return response.data as import('@/types/network-partner').NpNote;
  }

  async getBackofficePartnerStatus(
    partnerId: string
  ): Promise<import('@/types/network-partner').BackofficePartnerStatus> {
    const response = await this.instance.get(
      `/admin/network-partners/${partnerId}/backoffice-status`
    );
    return response.data as import('@/types/network-partner').BackofficePartnerStatus;
  }

  async createBackofficePartnerAccount(
    partnerId: string
  ): Promise<import('@/types/network-partner').BackofficePartnerStatus> {
    const response = await this.instance.post(
      `/admin/network-partners/${partnerId}/backoffice-account`
    );
    return response.data as import('@/types/network-partner').BackofficePartnerStatus;
  }

  async listSubjurisdictions(): Promise<import('@/types/network-partner').SubjurisdictionInfo[]> {
    const response = await this.instance.get('/jurisdictions/subjurisdictions');
    return response.data as import('@/types/network-partner').SubjurisdictionInfo[];
  }

  /** One page of the partner's engagement history (review aggregates
   * cover every engagement). */
  async getNetworkPartnerEngagements(
    partnerId: string,
    params: { page?: number; pageSize?: number } = {}
  ): Promise<import('@/types/network-partner').NpEngagements> {
    const response = await this.instance.get(`/admin/network-partners/${partnerId}/engagements`, {
      params: { page: params.page, page_size: params.pageSize },
    });
    return response.data as import('@/types/network-partner').NpEngagements;
  }

  // ---------------------------------------------------------------
  // Screening outcomes — the hit rating and the findings behind it
  // ---------------------------------------------------------------

  async getScreeningOutcome(
    reportId: string
  ): Promise<import('@/types/screening-outcome').ReportScreeningOutcome> {
    const { transformToReportScreeningOutcome } = await import('@/types/screening-outcome');
    const response = await this.instance.get(`/reports/${reportId}/screening-outcome`);
    return transformToReportScreeningOutcome(response.data);
  }

  // Risk profiles — admin-only rating policies
  // ---------------------------------------------------------------

  /** The seeded ISO country list (alpha-2/alpha-3/name), for pickers. */
  async listJurisdictions(): Promise<import('@/types/country').Country[]> {
    const response = await this.instance.get('/jurisdictions/');
    return response.data as import('@/types/country').Country[];
  }

  async listRiskProfiles(): Promise<import('@/types/risk-profile').RiskProfile[]> {
    const { transformToRiskProfile } = await import('@/types/risk-profile');
    const response = await this.instance.get('/risk-profiles');
    return (response.data as import('@/types/risk-profile').ApiRiskProfile[]).map(
      transformToRiskProfile
    );
  }

  async getRiskProfile(id: string): Promise<import('@/types/risk-profile').RiskProfile> {
    const { transformToRiskProfile } = await import('@/types/risk-profile');
    const response = await this.instance.get(`/risk-profiles/${id}`);
    return transformToRiskProfile(response.data);
  }

  async createRiskProfile(
    payload: import('@/types/risk-profile').RiskProfileCreatePayload
  ): Promise<import('@/types/risk-profile').RiskProfile> {
    const { transformToRiskProfile } = await import('@/types/risk-profile');
    const response = await this.instance.post('/risk-profiles', payload);
    return transformToRiskProfile(response.data);
  }

  async updateRiskProfile(
    id: string,
    payload: import('@/types/risk-profile').RiskProfileUpdatePayload
  ): Promise<import('@/types/risk-profile').RiskProfile> {
    const { transformToRiskProfile } = await import('@/types/risk-profile');
    const response = await this.instance.patch(`/risk-profiles/${id}`, payload);
    return transformToRiskProfile(response.data);
  }

  async deleteRiskProfile(id: string): Promise<void> {
    await this.instance.delete(`/risk-profiles/${id}`);
  }

  async getPlatformDefaultRiskProfile(): Promise<import('@/types/risk-profile').PlatformDefault> {
    const { transformToPlatformDefault } = await import('@/types/risk-profile');
    const response = await this.instance.get('/risk-profiles/platform-default');
    return transformToPlatformDefault(response.data);
  }

  async getBuiltinDefaultRiskProfile(): Promise<import('@/types/risk-profile').PlatformDefault> {
    const { transformToPlatformDefault } = await import('@/types/risk-profile');
    const response = await this.instance.get('/risk-profiles/builtin-default');
    return transformToPlatformDefault(response.data);
  }

  async putPlatformDefaultRiskProfile(payload: {
    rules: import('@/types/risk-profile').RatingRule[];
    description?: string | null;
    high_risk_jurisdictions?: string[];
  }): Promise<import('@/types/risk-profile').PlatformDefault> {
    const { transformToPlatformDefault } = await import('@/types/risk-profile');
    const response = await this.instance.put('/risk-profiles/platform-default', payload);
    return transformToPlatformDefault(response.data);
  }

  // Screening Batches — admin bulk basic-screening runs
  // ---------------------------------------------------------------

  /** Newest-first batch list. Conditional: see `ConditionalRequestOptions`. */
  async listScreeningBatches(
    opts: { teamId?: string; limit?: number; offset?: number } = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<import('@/types/screening-batch').ScreeningBatchSummary[]>> {
    return this.getConditional<import('@/types/screening-batch').ScreeningBatchSummary[]>(
      '/screening-batches',
      {
        team_id: opts.teamId || undefined,
        limit: opts.limit,
        offset: opts.offset,
      },
      options
    );
  }

  /** Batch header + whole-batch counts + ONE page of report rows.
   * Conditional: see `ConditionalRequestOptions`. */
  async getScreeningBatch(
    batchId: string,
    params: import('@/types/screening-batch').ScreeningBatchPageParams = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<import('@/types/screening-batch').ScreeningBatchDetail>> {
    return this.getConditional<import('@/types/screening-batch').ScreeningBatchDetail>(
      `/screening-batches/${batchId}`,
      {
        page: params.page,
        page_size: params.pageSize,
        rating: params.rating || undefined,
        hrj: params.hrj,
        search: params.search || undefined,
        status: params.status || undefined,
        sort: params.sort || undefined,
      },
      options
    );
  }

  /** Kick off the whole-batch zip build (register.csv + every PDF).
   * 409 while one is already running — poll getScreeningBatchExport. */
  async startScreeningBatchExport(
    batchId: string
  ): Promise<import('@/types/screening-batch').ScreeningBatchExportStatus> {
    const response = await this.instance.post(`/screening-batches/${batchId}/export`);
    return response.data as import('@/types/screening-batch').ScreeningBatchExportStatus;
  }

  /** The export manifest; carries a fresh presigned URL when complete. */
  async getScreeningBatchExport(
    batchId: string
  ): Promise<import('@/types/screening-batch').ScreeningBatchExportStatus> {
    const response = await this.instance.get(`/screening-batches/${batchId}/export`);
    return response.data as import('@/types/screening-batch').ScreeningBatchExportStatus;
  }

  /** Atomic bulk create — a 422 with per-row errors surfaces as
   * ApiValidationError with `detail.row_errors`.
   *
   * Bypasses the response interceptor (as the config import does): the
   * interceptor flattens every error body to a string, which is how the
   * per-row list never reached the upload page. */
  async createScreeningBatch(
    payload: import('@/types/screening-batch').ScreeningBatchCreatePayload
  ): Promise<import('@/types/screening-batch').ScreeningBatchSummary> {
    const response = await this.instance.post('/screening-batches', payload, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
      validateStatus: () => true,
    });
    if (response.status >= 200 && response.status < 300) {
      return response.data as import('@/types/screening-batch').ScreeningBatchSummary;
    }

    const data = response.data as
      | { error?: string; detail?: string | unknown[] | Record<string, unknown> }
      | undefined;
    const detail = data?.detail;
    if (
      response.status === 422 &&
      detail &&
      typeof detail === 'object' &&
      !Array.isArray(detail) &&
      Array.isArray((detail as { row_errors?: unknown }).row_errors)
    ) {
      throw new ApiValidationError((detail.message as string) ?? 'Validation failed', detail);
    }
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 500 && !config.isDevelopment) {
      throw new Error('Server error');
    }
    // The interceptor's wording for every other body: a string detail, a
    // written message, else the body (FastAPI's 422 list) as JSON.
    const message =
      data?.error ??
      (typeof detail === 'string' ? detail : null) ??
      (detail && typeof detail === 'object' && !Array.isArray(detail)
        ? (detail.message as string | undefined)
        : null) ??
      (detail != null ? JSON.stringify(detail) : null) ??
      `Error ${response.status}`;
    throw new Error(message);
  }

  /** Adjust the dispatch window live (applies at the next top-up). */
  async updateScreeningBatch(
    batchId: string,
    patch: { max_in_flight: number }
  ): Promise<import('@/types/screening-batch').ScreeningBatchSummary> {
    const response = await this.instance.patch(`/screening-batches/${batchId}`, patch);
    return response.data as import('@/types/screening-batch').ScreeningBatchSummary;
  }

  async startScreeningBatch(
    batchId: string
  ): Promise<import('@/types/screening-batch').ScreeningBatchSummary> {
    const response = await this.instance.post(`/screening-batches/${batchId}/start`);
    return response.data as import('@/types/screening-batch').ScreeningBatchSummary;
  }

  async pauseScreeningBatch(
    batchId: string
  ): Promise<import('@/types/screening-batch').ScreeningBatchSummary> {
    const response = await this.instance.post(`/screening-batches/${batchId}/pause`);
    return response.data as import('@/types/screening-batch').ScreeningBatchSummary;
  }

  async resumeScreeningBatch(
    batchId: string
  ): Promise<import('@/types/screening-batch').ScreeningBatchSummary> {
    const response = await this.instance.post(`/screening-batches/${batchId}/resume`);
    return response.data as import('@/types/screening-batch').ScreeningBatchSummary;
  }

  async reviewBackofficeAssignment(
    assignmentId: string,
    body: import('@/types/network-partner').NpReviewPayload
  ): Promise<import('@/types/np-assignments').BackofficeAssignment> {
    const response = await this.instance.post(
      `/backoffice-assignments/${assignmentId}/review`,
      body
    );
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  // ---------------------------------------------------------------
  // Triage Plan panel — read-only research-plan items
  // ---------------------------------------------------------------

  async listResearchPlanItems(
    reportId: string
  ): Promise<import('@/types/research-plan').ResearchPlanResponse> {
    const response = await this.instance.get(`/reports/${reportId}/research-plan-items`);
    return response.data as import('@/types/research-plan').ResearchPlanResponse;
  }

  // Generic request methods
  get(url: string, config?: any) {
    return this.instance.get(url, config);
  }

  post(url: string, data?: any, config?: any) {
    return this.instance.post(url, data, config);
  }

  put(url: string, data?: any, config?: any) {
    return this.instance.put(url, data, config);
  }

  patch(url: string, data?: any, config?: any) {
    return this.instance.patch(url, data, config);
  }

  delete(url: string, config?: any) {
    return this.instance.delete(url, config);
  }

  // ==========================================================================
  // Chat (Phase 07)
  // ==========================================================================

  async listChatThreads(params: {
    entityType: 'enquiry' | 'report' | 'report_request';
    entityId: string;
  }): Promise<ChatThread[]> {
    const response = await this.instance.get('/chat/threads', {
      params: { entity_type: params.entityType, entity_id: params.entityId },
    });
    return (response.data as Parameters<typeof transformToChatThread>[0][]).map(
      transformToChatThread
    );
  }

  async createChatThread(params: {
    entityType: 'enquiry' | 'report' | 'report_request';
    entityId: string;
    kind: ChatThreadKind | string;
    npId?: string;
  }): Promise<ChatThread> {
    const response = await this.instance.post('/chat/threads', null, {
      params: {
        entity_type: params.entityType,
        entity_id: params.entityId,
        kind: params.kind,
        np_id: params.npId,
      },
    });
    return transformToChatThread(response.data);
  }

  /** A page of the thread's messages (reverse-chronological). Polled by
   * the chat panel — conditional: see `ConditionalRequestOptions`. */
  async listChatMessages(
    threadId: string,
    params?: { limit?: number; beforeId?: string; includeInternal?: boolean },
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<ChatMessage[]>> {
    return this.getConditional(
      `/chat/threads/${threadId}/messages`,
      {
        limit: params?.limit,
        before_id: params?.beforeId,
        // Admin merged view: fold the internal sibling's notes in.
        include_internal: params?.includeInternal || undefined,
      },
      options,
      (raw) => (raw as Parameters<typeof transformToChatMessage>[0][]).map(transformToChatMessage)
    );
  }

  async sendChatMessage(threadId: string, body: ChatSendRequest): Promise<ChatMessage> {
    const response = await this.instance.post(`/chat/threads/${threadId}/messages`, {
      body_text: body.bodyText,
      is_private: body.isPrivate,
      attachment_ids: body.attachmentIds ?? [],
    });
    return transformToChatMessage(response.data);
  }

  async markChatThreadSeen(threadId: string, upToMessageId: string): Promise<void> {
    await this.instance.post(`/chat/threads/${threadId}/seen`, {
      up_to_message_id: upToMessageId,
    });
  }

  // ---- Conversation attachments ----

  /** Multipart upload of one file to a thread. Zips are unpacked
   *  server-side; the response includes the parent + any extracted
   *  members. `context` names the page the upload came from: the client
   *  conversation is one thread per case, so the thread alone no longer
   *  says which request a client's file is about — the page does (a
   *  client file sent from a submitted request's page auto-attaches to
   *  that request). */
  async uploadConversationAttachment(
    threadId: string,
    file: File,
    chatMessageId?: string,
    context?: ConversationUploadContext
  ): Promise<ConversationAttachment[]> {
    const formData = new FormData();
    formData.append('files', file);
    if (chatMessageId) {
      formData.append('chat_message_id', chatMessageId);
    }
    if (context) {
      formData.append('context_entity_type', context.entityType);
      formData.append('context_entity_id', context.entityId);
    }
    const response = await this.instance.post(`/chat/threads/${threadId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: UPLOAD_TIMEOUT_MS,
    });
    return (response.data as ApiConversationAttachment[]).map(transformToConversationAttachment);
  }

  async listConversationAttachments(threadId: string): Promise<ConversationAttachment[]> {
    const response = await this.instance.get(`/chat/threads/${threadId}/attachments`);
    return (response.data as ApiConversationAttachment[]).map(transformToConversationAttachment);
  }

  /** Re-pull missed Freshdesk history + attachments for a thread on demand,
   *  returning the refreshed file list. Idempotent; for enquiry threads. */
  async resyncConversationAttachments(threadId: string): Promise<ConversationAttachment[]> {
    const response = await this.instance.post(`/chat/threads/${threadId}/resync`, undefined, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return (response.data as ApiConversationAttachment[]).map(transformToConversationAttachment);
  }

  /** Re-fetch a single attachment's presigned URL (staleness recovery). */
  /** Admin: flip a file between Client and Internal (zip children follow). */
  async updateAttachmentClassification(
    attachmentId: string,
    classification: 'client' | 'internal'
  ): Promise<ConversationAttachment> {
    const response = await this.instance.patch(`/chat/attachments/${attachmentId}/classification`, {
      classification,
    });
    return transformToConversationAttachment(response.data as ApiConversationAttachment);
  }

  async getConversationAttachmentDownloadUrl(attachmentId: string): Promise<string> {
    const response = await this.instance.get(`/chat/attachments/${attachmentId}/download-url`);
    return response.data.download_url as string;
  }

  // ---- Extraction (anywhere-in-conversation) ----

  async extractReportRequests(
    enquiryId: string,
    sourceSet: ExtractionSourceSet
  ): Promise<ExtractReportRequestsResponse> {
    const response = await this.instance.post(`/enquiries/${enquiryId}/extract`, sourceSet, {
      timeout: LONG_REQUEST_TIMEOUT_MS,
    });
    return response.data as ExtractReportRequestsResponse;
  }

  /** Convert (or, when the enquiry is already triaged, append) extracted
   *  candidates to RRs, recording provenance + attaching docs. Posts the full
   *  candidate payload (with attachment_ids + sources) to convert-to-requests
   *  or append-requests depending on the enquiry stage. */
  async extractConvertEnquiryToRequests(
    enquiryId: string,
    body: EnquiryExtractConvertRequest,
    append: boolean
  ): Promise<EnquiryConvertResponse> {
    const path = append
      ? `/enquiries/${enquiryId}/append-requests`
      : `/enquiries/${enquiryId}/convert-to-requests`;
    const response = await this.instance.post(path, body);
    return response.data as EnquiryConvertResponse;
  }

  // ---- Report-request attachments + provenance ----

  /** The request's attached documents. Polled while digests run —
   * conditional: see `ConditionalRequestOptions`. */
  async listReportRequestAttachments(
    rrId: string,
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<ReportRequestAttachment[]>> {
    return this.getConditional(`/report-requests/${rrId}/attachments`, undefined, options, (raw) =>
      (raw as ApiReportRequestAttachment[]).map(transformToReportRequestAttachment)
    );
  }

  async attachConversationFileToReportRequest(
    rrId: string,
    attachmentId: string
  ): Promise<ReportRequestAttachment> {
    const response = await this.instance.post(`/report-requests/${rrId}/attachments`, {
      conversation_attachment_id: attachmentId,
    });
    return transformToReportRequestAttachment(response.data as ApiReportRequestAttachment);
  }

  async detachReportRequestAttachment(rrId: string, attachmentId: string): Promise<void> {
    await this.instance.delete(`/report-requests/${rrId}/attachments/${attachmentId}`);
  }

  /** Re-run a failed file digest in the background; the rail polls the
   *  attachments list to watch it land. */
  async retryReportRequestAttachmentDigest(
    rrId: string,
    attachmentId: string
  ): Promise<ReportRequestAttachment> {
    const response = await this.instance.post(
      `/report-requests/${rrId}/attachments/${attachmentId}/retry-digest`
    );
    return transformToReportRequestAttachment(response.data as ApiReportRequestAttachment);
  }

  async listReportRequestSources(
    rrId: string,
    options?: RequestOptions
  ): Promise<ReportRequestSource[]> {
    const response = await this.instance.get(`/report-requests/${rrId}/sources`, {
      signal: options?.signal,
    });
    return (response.data as ApiReportRequestSource[]).map(transformToReportRequestSource);
  }

  // ---------------------------------------------------------------
  // Backoffice assignments — assignment-centric NP triage
  // ---------------------------------------------------------------

  async listBackofficeAssignments(reportId: string): Promise<ReportAssignments> {
    const response = await this.instance.get(`/reports/${reportId}/backoffice-assignments`);
    return transformToReportAssignments(response.data as ApiReportAssignments);
  }

  async dispatchBackofficeAssignments(reportId: string): Promise<void> {
    await this.instance.post(`/reports/${reportId}/backoffice-assignments/dispatch`);
  }

  /** Set a report's client price. `reason` is required (10+ characters) once
   * the report is completed — its figures may already have been billed. */
  async patchReportPricing(
    reportId: string,
    priceOverride: number | null,
    reason?: string | null
  ): Promise<ReportResponse> {
    const body: Record<string, unknown> = { price_override: priceOverride };
    if (reason != null) {
      body.reason = reason;
    }
    const { data } = await this.instance.patch<ReportResponse>(
      `/reports/${reportId}/pricing`,
      body
    );
    return data;
  }

  /** Drop jurisdictions from a started report and from its Backoffice project.
   * `reason` is always required (10+ characters). Admin-only. */
  async removeReportJurisdictions(
    reportId: string,
    jurisdictionCodes: string[],
    reason: string
  ): Promise<ReportResponse> {
    const { data } = await this.instance.post<ReportResponse>(
      `/reports/${reportId}/jurisdictions/remove`,
      { jurisdiction_codes: jurisdictionCodes, reason }
    );
    return data;
  }

  /** Every change to a report's commercials (its partner assignments' too),
   * newest first. Admin-only. */
  async listCommercialChanges(reportId: string): Promise<CommercialChange[]> {
    const { data } = await this.instance.get<ApiCommercialChangesResponse>(
      `/reports/${reportId}/commercial-changes`
    );
    return (data.changes ?? []).map(transformToCommercialChange);
  }

  /** Whether changing this report's figures could touch money that has
   * already been billed. Admin-only. */
  async getReportBillingStatus(reportId: string): Promise<ReportBillingStatus> {
    const { data } = await this.instance.get<ApiReportBillingStatus>(
      `/reports/${reportId}/billing-status`
    );
    return transformToReportBillingStatus(data);
  }

  // Provider rate card and prepaid agreements. Admin-only. A rate that would
  // overlap another for the same key, or an edit to a rate an agreement owns,
  // is a 409 whose message names the problem.

  /** The rate card, optionally one provider's or only the rates in effect
   * on a day. Conditional: see `ConditionalRequestOptions`. */
  async listProviderRates(
    params: { provider?: string; activeOn?: string } = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<ProviderRate[]>> {
    return this.getConditional(
      '/provider-rates',
      { provider: params.provider, active_on: params.activeOn },
      options,
      (raw) => (raw as ApiProviderRate[]).map(transformToProviderRate)
    );
  }

  async createProviderRate(input: ProviderRateInput): Promise<ProviderRate> {
    const { data } = await this.instance.post<ApiProviderRate>(
      '/provider-rates',
      toApiProviderRateCreate(input)
    );
    return transformToProviderRate(data);
  }

  /** Correct a hand-entered rate. */
  async updateProviderRate(id: string, update: ProviderRateUpdate): Promise<ProviderRate> {
    const { data } = await this.instance.patch<ApiProviderRate>(
      `/provider-rates/${id}`,
      toApiProviderRateUpdate(update)
    );
    return transformToProviderRate(data);
  }

  async deleteProviderRate(id: string): Promise<void> {
    await this.instance.delete(`/provider-rates/${id}`);
  }

  /** Every prepaid agreement with its lines, optionally one provider's.
   * Conditional: see `ConditionalRequestOptions`. */
  async listProviderAgreements(
    params: { provider?: string } = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<ProviderAgreement[]>> {
    return this.getConditional(
      '/provider-agreements',
      { provider: params.provider },
      options,
      (raw) => (raw as ApiProviderAgreement[]).map(transformToProviderAgreement)
    );
  }

  async getProviderAgreement(id: string): Promise<ProviderAgreement> {
    const { data } = await this.instance.get<ApiProviderAgreement>(`/provider-agreements/${id}`);
    return transformToProviderAgreement(data);
  }

  /** Record an agreement; its lines' rates are added to the rate card. */
  async createProviderAgreement(input: ProviderAgreementInput): Promise<ProviderAgreement> {
    const { data } = await this.instance.post<ApiProviderAgreement>(
      '/provider-agreements',
      toApiProviderAgreementWrite(input)
    );
    return transformToProviderAgreement(data);
  }

  /** Save a whole agreement: its lines and the rates derived from them are replaced. */
  async replaceProviderAgreement(
    id: string,
    input: ProviderAgreementInput
  ): Promise<ProviderAgreement> {
    const { data } = await this.instance.put<ApiProviderAgreement>(
      `/provider-agreements/${id}`,
      toApiProviderAgreementWrite(input)
    );
    return transformToProviderAgreement(data);
  }

  /** Remove an agreement and the rates it put on the rate card. */
  async deleteProviderAgreement(id: string): Promise<void> {
    await this.instance.delete(`/provider-agreements/${id}`);
  }

  // Client contracts and their drawdown (the usage tracker). Admin-only.

  /** Every matching contract with its drawdown summary. Conditional: see
   * `ConditionalRequestOptions`. */
  async listClientContracts(
    params: { teamId?: string; activeOn?: string } = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<ContractSummary[]>> {
    return this.getConditional(
      '/client-contracts',
      { team_id: params.teamId, active_on: params.activeOn },
      options,
      (raw) => (raw as ApiContractSummary[]).map(transformToContractSummary)
    );
  }

  /** A contract with every item in its window, in drawdown order. */
  async getClientContract(id: string, options?: RequestOptions): Promise<ContractDetail> {
    const { data } = await this.instance.get<ApiContractDetail>(`/client-contracts/${id}`, {
      signal: options?.signal,
    });
    return transformToContractDetail(data);
  }

  /**
   * The Hub's drawdown against Backoffice's, read live from Backoffice.
   *
   * A 502 — Backoffice could not be read — is taken here rather than by the
   * response interceptor, which hides a 5xx's message outside development:
   * it rejects with `BackofficeUnreadableError` carrying the server's reason.
   */
  async getContractReconciliation(
    id: string,
    options?: RequestOptions
  ): Promise<ContractReconciliation> {
    const response = await this.instance.get(`/client-contracts/${id}/reconciliation`, {
      signal: options?.signal,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 502,
    });
    if (response.status === 502) {
      throw new BackofficeUnreadableError(
        detailMessage(
          (response.data as { detail?: unknown } | undefined)?.detail,
          'Backoffice could not be read'
        )
      );
    }
    return transformToContractReconciliation(response.data as ApiContractReconciliation);
  }

  // Revenue against cost (the COGS dashboard). Admin-only. Every amount read
  // is also given in `displayCurrency`, converted at its own date.

  /** One row per piece of consumed work, newest first. Conditional: see
   * `ConditionalRequestOptions`. */
  async listProjectEconomics(
    query: ProjectEconomicsQuery,
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<ProjectEconomicsPage>> {
    return this.getConditional(
      '/economics/projects',
      {
        display_currency: query.displayCurrency,
        team_id: query.teamId,
        period_from: query.periodFrom,
        period_to: query.periodTo,
        source: query.source,
        report_config_id: query.reportConfigId,
        include_voided: query.includeVoided,
        page: query.page,
        page_size: query.pageSize,
      },
      options,
      (raw) => transformToProjectEconomicsPage(raw as ApiProjectEconomicsPage)
    );
  }

  /** One piece of work with every cost line behind it. */
  async getProjectEconomics(
    source: EconomicsSource,
    sourceRef: string,
    displayCurrency: DisplayCurrency,
    options?: RequestOptions
  ): Promise<ProjectEconomicsDetail> {
    const { data } = await this.instance.get<ApiProjectEconomicsDetail>(
      `/economics/projects/${source}/${encodeURIComponent(sourceRef)}`,
      { params: { display_currency: displayCurrency }, signal: options?.signal }
    );
    return transformToProjectEconomicsDetail(data);
  }

  /** Totals by month, provider and team, and how far the costs can be
   * trusted. Conditional: see `ConditionalRequestOptions`. */
  async getEconomicsSummary(
    query: EconomicsQuery,
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<EconomicsSummary>> {
    return this.getConditional(
      '/economics/summary',
      {
        display_currency: query.displayCurrency,
        team_id: query.teamId,
        period_from: query.periodFrom,
        period_to: query.periodTo,
      },
      options,
      (raw) => transformToEconomicsSummary(raw as ApiEconomicsSummary)
    );
  }

  /** Costs no report or project is behind, such as an unused pool. */
  async getUnattributedCosts(
    query: Omit<EconomicsQuery, 'teamId'>,
    options?: RequestOptions
  ): Promise<UnattributedCosts> {
    const { data } = await this.instance.get<ApiUnattributedCosts>('/economics/unattributed', {
      params: {
        display_currency: query.displayCurrency,
        period_from: query.periodFrom,
        period_to: query.periodTo,
      },
      signal: options?.signal,
    });
    return transformToUnattributedCosts(data);
  }

  /** Refresh the contracts mirror and both ledgers now. Can take a while:
   * it reads every completed Backoffice project and recomputes every cost. */
  async refreshEconomics(): Promise<EconomicsRefreshResult> {
    const { data } = await this.instance.post<ApiEconomicsRefreshResult>(
      '/economics/refresh',
      undefined,
      { timeout: LONG_REQUEST_TIMEOUT_MS }
    );
    return transformToEconomicsRefreshResult(data);
  }

  /** Set report-level docx fill variables (plan 15 / B8). Admin-only, full replace. */
  async updateReportMetadata(
    reportId: string,
    reportMetadata: Record<string, unknown>
  ): Promise<ReportResponse> {
    const { data } = await this.instance.patch<ReportResponse>(`/reports/${reportId}/metadata`, {
      report_metadata: reportMetadata,
    });
    return data;
  }

  async createBackofficeAssignment(
    reportId: string,
    planItemIds: string[]
  ): Promise<BackofficeAssignment> {
    const response = await this.instance.post(`/reports/${reportId}/backoffice-assignments`, {
      plan_item_ids: planItemIds,
    });
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  /** Create a legacy-flow assignment from Django-template scope components
   * (the manual flow's unit vocabulary) instead of HUMAN plan items. */
  async createBackofficeTemplateAssignment(
    reportId: string,
    templateItems: {
      scope_component_slug: string;
      subject_id: string | null;
      jurisdiction_code: string | null;
    }[]
  ): Promise<BackofficeAssignment> {
    const response = await this.instance.post(`/reports/${reportId}/backoffice-assignments`, {
      template_items: templateItems,
    });
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  /**
   * Edit an assignment's commercials. A fee on an assignment locked to
   * Armadillo is refused with a 409 `registry_fee_managed`: the Hub writes
   * that fee from the registry orders, and `setRegistryFeeAdjustment` is
   * the lever.
   */
  async updateBackofficeAssignment(
    assignmentId: string,
    patch: BackofficeAssignmentUpdate
  ): Promise<BackofficeAssignment> {
    // Map camelCase patch → snake_case wire; only send provided keys.
    const body: Record<string, unknown> = {};
    if ('npFee' in patch) {
      body.np_fee = patch.npFee;
    }
    if ('npCurrency' in patch) {
      body.np_currency = patch.npCurrency;
    }
    if ('npDueDate' in patch) {
      body.np_due_date = patch.npDueDate;
    }
    if ('turnaroundTimeDays' in patch) {
      body.turnaround_time_days = patch.turnaroundTimeDays;
    }
    if ('notes' in patch) {
      body.notes = patch.notes;
    }
    if (patch.reason != null) {
      body.reason = patch.reason;
    }
    const response = await this.instance.patch(`/backoffice-assignments/${assignmentId}`, body);
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  /**
   * Admin: set, change or clear the manual adjustment on an Armadillo
   * assignment's fee, which the order rules otherwise write alone: the
   * automatic assignment, or a manual one locked to Armadillo while it is
   * live. The amount travels as a 2 dp string so pence survive the wire;
   * null clears it. The fee is worked out again before the response, and
   * Backoffice is brought in step in the background. On a row whose orders
   * all closed, the fee is the adjustment alone. A 409 is
   * `registry_not_armadillo` (neither of those: the fee is edited
   * directly) or `registry_taken_over` (a manual assignment bills its
   * orders now).
   */
  async setRegistryFeeAdjustment(
    assignmentId: string,
    request: RegistryFeeAdjustmentRequest
  ): Promise<BackofficeAssignment> {
    const response = await this.instance.patch(
      `/backoffice-assignments/${assignmentId}/registry-fee-adjustment`,
      {
        amount: request.amount === null ? null : request.amount.toFixed(2),
        reason: request.reason,
      }
    );
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  async addBackofficeAssignmentItems(
    assignmentId: string,
    planItemIds: string[]
  ): Promise<BackofficeAssignment> {
    const response = await this.instance.post(`/backoffice-assignments/${assignmentId}/items`, {
      plan_item_ids: planItemIds,
    });
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  /** Clone an assignment's scope into a new NP-unassigned assignment
   *  (N partners over the same scope). */
  async duplicateBackofficeAssignment(assignmentId: string): Promise<BackofficeAssignment> {
    const response = await this.instance.post(
      `/backoffice-assignments/${assignmentId}/duplicate`,
      {}
    );
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  /**
   * Send ONE assignment to Backoffice on a report that has already started —
   * how a partner is added late. The partner request is created on the
   * existing project and activated on its own. Idempotent: if the request was
   * created but activating it failed (a 502 saying so), calling this again
   * only activates, so the partner is never created or emailed twice.
   */
  async dispatchBackofficeAssignment(assignmentId: string): Promise<BackofficeAssignment> {
    const response = await this.instance.post(`/backoffice-assignments/${assignmentId}/dispatch`);
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  /**
   * Cancel a dispatched assignment — drop a partner who will not deliver, or
   * who is being replaced. The partner request is cancelled in Backoffice first
   * (the partner is emailed if they had been told about it), so a failure there
   * changes nothing. The assignment, its files and its fee are kept; its plan
   * items are skipped so nothing waits on them. `reason` is required (10+
   * characters) and stored with who and when. Idempotent.
   */
  async cancelBackofficeAssignment(
    assignmentId: string,
    reason: string
  ): Promise<BackofficeAssignment> {
    const response = await this.instance.post(`/backoffice-assignments/${assignmentId}/cancel`, {
      reason,
    });
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  /** Returns the updated assignment, or null when removing the last item
   *  auto-deleted the assignment. */
  async removeBackofficeAssignmentItem(
    assignmentId: string,
    planItemId: string
  ): Promise<BackofficeAssignment | null> {
    const response = await this.instance.delete(
      `/backoffice-assignments/${assignmentId}/items/${planItemId}`
    );
    const data = response.data as { deleted?: boolean } | ApiBackofficeAssignment;
    if (data && (data as { deleted?: boolean }).deleted) {
      return null;
    }
    return transformToBackofficeAssignment(data as ApiBackofficeAssignment);
  }

  async deleteBackofficeAssignment(assignmentId: string): Promise<void> {
    await this.instance.delete(`/backoffice-assignments/${assignmentId}`);
  }

  async lockBackofficeAssignmentNp(
    assignmentId: string,
    networkPartnerId: string,
    opts?: { npFee?: number | null; npDueDate?: string | null; turnaroundTimeDays?: number | null }
  ): Promise<BackofficeAssignment> {
    const body: Record<string, unknown> = { network_partner_id: networkPartnerId };
    if (opts?.npFee !== undefined) {
      body.np_fee = opts.npFee;
    }
    if (opts?.npDueDate !== undefined) {
      body.np_due_date = opts.npDueDate;
    }
    if (opts?.turnaroundTimeDays !== undefined) {
      body.turnaround_time_days = opts.turnaroundTimeDays;
    }
    const response = await this.instance.post(`/backoffice-assignments/${assignmentId}/lock`, body);
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  async unlockBackofficeAssignmentNp(assignmentId: string): Promise<BackofficeAssignment> {
    const response = await this.instance.post(`/backoffice-assignments/${assignmentId}/unlock`);
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  async addBackofficeAssignmentShortlistNp(
    assignmentId: string,
    networkPartnerId: string
  ): Promise<BackofficeAssignment> {
    const response = await this.instance.post(`/backoffice-assignments/${assignmentId}/shortlist`, {
      network_partner_id: networkPartnerId,
    });
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  async removeBackofficeAssignmentShortlistNp(
    assignmentId: string,
    npId: string
  ): Promise<BackofficeAssignment> {
    const response = await this.instance.delete(
      `/backoffice-assignments/${assignmentId}/shortlist/${npId}`
    );
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  /** Share the brief + attached documents with one shortlisted candidate
   * (silent partner-portal visibility — no email; tell them in chat). */
  async sharePreviewShortlistNp(assignmentId: string, npId: string): Promise<BackofficeAssignment> {
    const response = await this.instance.post(
      `/backoffice-assignments/${assignmentId}/shortlist/${npId}/preview`
    );
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  /** Withdraw a candidate's document preview (keeps them shortlisted). */
  async withdrawPreviewShortlistNp(
    assignmentId: string,
    npId: string
  ): Promise<BackofficeAssignment> {
    const response = await this.instance.delete(
      `/backoffice-assignments/${assignmentId}/shortlist/${npId}/preview`
    );
    return transformToBackofficeAssignment(response.data as ApiBackofficeAssignment);
  }

  async attachBackofficeAssignmentFile(
    assignmentId: string,
    conversationAttachmentId: string
  ): Promise<void> {
    await this.instance.post(`/backoffice-assignments/${assignmentId}/attachments`, {
      conversation_attachment_id: conversationAttachmentId,
    });
  }

  async detachBackofficeAssignmentFile(
    assignmentId: string,
    conversationAttachmentId: string
  ): Promise<void> {
    await this.instance.delete(
      `/backoffice-assignments/${assignmentId}/attachments/${conversationAttachmentId}`
    );
  }

  /**
   * What may be enrolled into monitoring from this report.
   *
   * Answers with `available: false` and a reason rather than an error status
   * when nothing may — the caller is deciding whether to render an action, and
   * "this report ran no monitorable checks" is an answer, not a failure.
   */
  async getMonitoringEnrolmentOptions(reportId: string): Promise<MonitoringEnrolmentOptions> {
    const response = await this.instance.get(`/monitoring/reports/${reportId}/enrolment-options`);
    return transformToMonitoringEnrolmentOptions(response.data as ApiMonitoringEnrolmentOptions);
  }

  /**
   * Enrol a report's subjects into ongoing monitoring.
   *
   * The server re-derives what this report can offer rather than trusting the
   * payload, so a stale tab gets a 400 instead of a subscription to something
   * the report never ran.
   */
  async createMonitoringEnrolment(payload: {
    reportId: string;
    subjectIds: string[];
    componentSlugs: string[];
  }): Promise<MonitoringEnrolmentResult> {
    const response = await this.instance.post('/monitoring/enrolments', {
      report_id: payload.reportId,
      subject_ids: payload.subjectIds,
      component_slugs: payload.componentSlugs,
    });
    return transformToMonitoringEnrolmentResult(response.data as ApiMonitoringEnrolmentResult);
  }

  /**
   * A page of the analyst alert queue, with ETag-aware polling.
   *
   * Pass the ETag from a previous response via `options.etag` and the request
   * carries `If-None-Match`; an unchanged page answers 304 with no body, which
   * this surfaces as the literal `'not-modified'` so pollers can skip the
   * state update. Omit it to force a full read.
   *
   * Seeded alerts are excluded server-side unless `includeSeeded` is set —
   * they were adjudicated in the report they came from and number in the
   * hundreds per enrolment.
   */
  async listMonitoringAlerts(
    query: MonitoringAlertQuery = {},
    options?: { etag?: string | null }
  ): Promise<{ data: MonitoringAlertPage; etag: string | null } | 'not-modified'> {
    // Only set params the caller gave: an explicit `state=` with an empty
    // value filters to alerts whose state is the empty string, which matches
    // nothing and reads on screen as "no alerts".
    const params: Record<string, string | number | boolean> = {};
    if (query.page !== undefined) {
      params.page = query.page;
    }
    if (query.pageSize !== undefined) {
      params.page_size = query.pageSize;
    }
    if (query.state) {
      params.state = query.state;
    }
    if (query.severity) {
      params.severity = query.severity;
    }
    if (query.scopeComponentSlug) {
      params.scope_component_slug = query.scopeComponentSlug;
    }
    if (query.monitoredSubjectId) {
      params.monitored_subject_id = query.monitoredSubjectId;
    }
    if (query.includeSeeded) {
      params.include_seeded = query.includeSeeded;
    }

    const response = await this.instance.get('/monitoring/alerts', {
      params,
      headers: options?.etag ? { 'If-None-Match': options.etag } : undefined,
      // Axios rejects non-2xx by default; 304 is the expected steady state
      // here, not an error.
      validateStatus: (status) => (status >= 200 && status < 300) || status === 304,
    });

    if (response.status === 304) {
      return 'not-modified';
    }
    return {
      data: transformToMonitoringAlertPage(response.data as ApiMonitoringAlertPage),
      etag: (response.headers.etag as string | undefined) ?? null,
    };
  }

  // ---- Monitoring entitlements (admin) ----

  /** Admin: every team that has, or had, monitoring. Conditional on `options.etag`. */
  async listMonitoringEntitlements(
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<MonitoringEntitlement[]>> {
    return this.getConditional('/monitoring/entitlements', undefined, options, (raw) =>
      (raw as ApiMonitoringEntitlement[]).map(transformToMonitoringEntitlement)
    );
  }

  /**
   * Admin: grant monitoring, or edit an entitlement's contract ref and notes.
   * Both fields are replaced, so pass the current value to keep one. Never
   * changes state — that is `changeMonitoringEntitlement`.
   */
  async putMonitoringEntitlement(
    teamId: string,
    details: { contractRef: string | null; notes: string | null }
  ): Promise<MonitoringEntitlement> {
    const response = await this.instance.put(
      `/monitoring/entitlements/${encodeURIComponent(teamId)}`,
      { contract_ref: details.contractRef, notes: details.notes }
    );
    return transformToMonitoringEntitlement(response.data as ApiMonitoringEntitlement);
  }

  /**
   * Admin: suspend, resume or end a team's monitoring. Resolves with what the
   * provider half managed even when it partly failed — the state change has
   * already happened by then.
   */
  async changeMonitoringEntitlement(
    teamId: string,
    action: MonitoringEntitlementAction
  ): Promise<MonitoringEntitlementChange> {
    const response = await this.instance.post(
      `/monitoring/entitlements/${encodeURIComponent(teamId)}/${action}`
    );
    return transformToMonitoringEntitlementChange(response.data as ApiMonitoringEntitlementChange);
  }

  // ---- Monitored subjects (admin) ----

  /** Admin: a page of monitored subjects, conditional on `options.etag`. */
  async listMonitoredSubjects(
    query: MonitoredSubjectQuery = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<MonitoredSubjectPage>> {
    return this.getConditional(
      '/monitoring/subjects',
      {
        page: query.page,
        page_size: query.pageSize,
        team_id: query.teamId || undefined,
        status: query.status || undefined,
        q: query.q || undefined,
      },
      options,
      (raw) => transformToMonitoredSubjectPage(raw as ApiMonitoredSubjectPage)
    );
  }

  /**
   * Admin: stop monitoring one subject. Resolves with whether Castellum
   * removed it even when it did not — the cancellation is already recorded.
   */
  async cancelMonitoredSubject(
    subjectId: string,
    payload: { reason: MonitoringCancellationReason; note: string | null }
  ): Promise<SubjectCancellation> {
    const response = await this.instance.post(
      `/monitoring/subjects/${encodeURIComponent(subjectId)}/cancel`,
      { reason: payload.reason, note: payload.note }
    );
    return transformToSubjectCancellation(response.data as ApiSubjectCancellation);
  }

  /**
   * Admin: register a pending subject with Castellum now, rather than at the
   * worker's next retry. Resolves even when Castellum refuses again, with the
   * reason — the subject just stays pending. A 409 means it is no longer
   * pending, or its team's monitoring is not active.
   */
  async registerMonitoredSubject(subjectId: string): Promise<SubjectRegistration> {
    const response = await this.instance.post(
      `/monitoring/subjects/${encodeURIComponent(subjectId)}/register`
    );
    return transformToSubjectRegistration(response.data as ApiSubjectRegistration);
  }

  // ---- Monitoring, as a client sees it ----

  /** The caller's team's monitored subjects. Conditional on `options.etag`. */
  async listClientMonitoredSubjects(
    query: TeamSubjectQuery = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<TeamSubjectPage>> {
    return this.getConditional(
      '/client/monitoring/subjects',
      {
        page: query.page,
        page_size: query.pageSize,
        status: query.status || undefined,
        q: query.q || undefined,
      },
      options,
      (raw) => transformToTeamSubjectPage(raw as ApiTeamSubjectPage)
    );
  }

  /**
   * One monitored subject and a page of the findings its team was sent.
   * Another team's subject is a 404. Conditional on `options.etag`.
   */
  async getClientMonitoredSubject(
    subjectId: string,
    query: { page?: number; pageSize?: number } = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<TeamSubjectDetail>> {
    return this.getConditional(
      `/client/monitoring/subjects/${encodeURIComponent(subjectId)}`,
      { page: query.page, page_size: query.pageSize },
      options,
      (raw) => transformToTeamSubjectDetail(raw as ApiTeamSubjectDetail)
    );
  }

  // ---- Registry documents (Armadillo) — admin ----

  /**
   * Find a company in Armadillo's (Dun & Bradstreet backed) index. One
   * criterion is enough; `country` is required with a registration number.
   * Free: nothing is ordered by searching.
   */
  async searchRegistryCompanies(
    params: RegistryCompanySearchParams,
    options?: RequestOptions
  ): Promise<RegistryCompanySearchResult> {
    const response = await this.instance.get('/registry-documents/search', {
      params: {
        name: params.name || undefined,
        country: params.country || undefined,
        duns: params.duns || undefined,
        registration_number: params.registration_number || undefined,
        page: params.page,
        report_id: params.report_id || undefined,
      },
      signal: options?.signal,
      timeout: LONG_REQUEST_TIMEOUT_MS,
      validateStatus: () => true,
    });
    this.throwProviderRead(response);
    return transformToRegistryCompanySearchResult(
      response.data as ApiRegistryCompanySearchResponse
    );
  }

  /** The documents Armadillo offers for a country, with price and turnaround. */
  async listRegistryProducts(
    country: string,
    options?: RequestOptions
  ): Promise<RegistryProduct[]> {
    const response = await this.instance.get('/registry-documents/products', {
      params: { country },
      signal: options?.signal,
      timeout: LONG_REQUEST_TIMEOUT_MS,
      validateStatus: () => true,
    });
    this.throwProviderRead(response);
    return (response.data as ApiRegistryProductsResponse).products.map(transformToRegistryProduct);
  }

  /**
   * Prepare an order on a report — sent to Armadillo when the report is
   * activated, or at once when it already is. Billable on delivery. Fails
   * with a `RegistryOrderError` whose `kind` says what happened and
   * whether an order now exists.
   */
  async createRegistryOrder(payload: RegistryOrderCreateRequest): Promise<RegistryOrderDetail> {
    return this.orderCall(() =>
      this.instance.post('/registry-documents/orders', payload, {
        validateStatus: () => true,
        timeout: REGISTRY_ORDER_TIMEOUT_MS,
      })
    );
  }

  /** A report's orders, newest first. Conditional on `options.etag`;
   * `status` narrows to one status (its own token space). */
  async listRegistryOrders(
    reportId: string,
    status?: RegistryOrderStatus | null,
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<RegistryOrderList>> {
    return this.getConditional(
      '/registry-documents/orders',
      { report_id: reportId, status: status ?? undefined },
      options,
      (raw) => transformToRegistryOrderList(raw as ApiRegistryOrderList)
    );
  }

  /** Admin: a page of every team's orders - the same endpoint read without
   * a report. Conditional on `options.etag`; each filter and page is its
   * own token space. */
  async listRegistryOrderQueue(
    query: RegistryOrderQueueQuery = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<RegistryOrderQueuePage>> {
    return this.getConditional(
      '/registry-documents/orders',
      {
        view: query.view,
        team_id: query.teamId || undefined,
        q: query.q || undefined,
        // Sent only when on: absent means "no restriction".
        late: query.late || undefined,
        page: query.page,
        page_size: query.pageSize,
      },
      options,
      (raw) => transformToRegistryOrderQueuePage(raw as ApiRegistryOrderQueuePage)
    );
  }

  /** One report's Armadillo conversation: every order it sent and the mail
   * on them, oldest first. Conditional on `options.etag`; the read carries
   * no download link, so a 304 never serves an expired one. */
  async getRegistryConversation(
    reportId: string,
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<RegistryOrderConversation>> {
    return this.getConditional(
      '/registry-documents/conversation',
      { report_id: reportId },
      options,
      (raw) => transformToRegistryConversation(raw as ApiRegistryOrderConversation)
    );
  }

  /** Admin: work out Armadillo's automatic assignment on a report again
   * now, and bring Backoffice in step with it, rather than waiting for the
   * delivery worker's next pass. A 409 (`partner_feature_off`) means the
   * feature is off in this environment. */
  async syncRegistryPartnerAssignment(reportId: string): Promise<RegistryPartnerSync> {
    const response = await this.instance.post(
      `/registry-documents/reports/${encodeURIComponent(reportId)}/partner-assignment/sync`
    );
    return transformToRegistryPartnerSync(response.data as ApiRegistryPartnerSync);
  }

  /** Admin: how many registry-document inbox items are still open - the
   * Admin menu badge. */
  async getRegistryAttention(options?: RequestOptions): Promise<RegistryAttention> {
    const response = await this.instance.get('/registry-documents/attention', {
      signal: options?.signal,
    });
    return transformToRegistryAttention(response.data as ApiRegistryAttention);
  }

  /** One order with its catalogue snapshot and the documents delivered so
   * far (each with a short-lived download link — re-read before use). */
  async getRegistryOrder(orderId: string): Promise<RegistryOrderDetail> {
    const response = await this.instance.get(
      `/registry-documents/orders/${encodeURIComponent(orderId)}`
    );
    return transformToRegistryOrderDetail(response.data as ApiRegistryOrderDetail);
  }

  /** Discard a prepared order — a draft nothing outside Nexus knows. A
   * sent order is refused (`kind: 'not_prepared'`): cancel it with Armadillo. */
  async discardRegistryOrder(orderId: string): Promise<void> {
    const response = await this.instance.delete(
      `/registry-documents/orders/${encodeURIComponent(orderId)}`,
      { validateStatus: () => true }
    );
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw registryOrderErrorFor(response.status, response.data);
    }
  }

  /** Send a prepared order now rather than waiting for the retry sweep —
   * for an activated report only. Same outcomes as creating an order. */
  async placeRegistryOrder(orderId: string): Promise<RegistryOrderDetail> {
    return this.orderCall(() =>
      this.instance.post(`/registry-documents/orders/${encodeURIComponent(orderId)}/place`, null, {
        validateStatus: () => true,
        timeout: REGISTRY_ORDER_TIMEOUT_MS,
      })
    );
  }

  /**
   * Close a sent order that will never be completed: a declined quote, an
   * order cancelled with the research desk. Nothing is sent to Armadillo.
   * A refusal (`kind: 'not_closeable'`) carries the API's own sentence.
   * A plain call rather than `orderCall`: no provider is waited on, so a
   * dropped connection is not "the order may have been placed".
   */
  async closeRegistryOrder(
    orderId: string,
    request: RegistryOrderCloseRequest
  ): Promise<RegistryOrderDetail> {
    const response = await this.instance.post(
      `/registry-documents/orders/${encodeURIComponent(orderId)}/close`,
      { reason: request.reason, note: request.note?.trim() || null },
      { validateStatus: () => true }
    );
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw registryOrderErrorFor(response.status, response.data);
    }
    return transformToRegistryOrderDetail(response.data as ApiRegistryOrderDetail);
  }

  /**
   * Record the desk's quote on its own, for one accepted or received by
   * email outside the Hub: the price for the row, and the turnaround for
   * the expected date that lets the order be chased when it is late.
   * Naming the desk's message (`answers_message_id`) makes it read as
   * answered. A refusal is typed for the quote call: `quote_not_allowed`
   * (409: the order's state), `validation` with `code` `expected_at_set`
   * or `unknown_message` (422), `not_found`. A plain call, like close: no
   * provider is waited on.
   */
  async recordRegistryQuote(
    orderId: string,
    request: RegistryOrderQuoteRecordRequest
  ): Promise<RegistryOrderDetail> {
    const response = await this.instance.post(
      `/registry-documents/orders/${encodeURIComponent(orderId)}/quote`,
      {
        price: request.price,
        currency: request.currency,
        turnaround_days: request.turnaround_days,
        answers_message_id: request.answers_message_id ?? null,
        note: request.note?.trim() || null,
      },
      { validateStatus: () => true }
    );
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw registryOrderErrorFor(response.status, response.data, 'quote');
    }
    return transformToRegistryOrderDetail(response.data as ApiRegistryOrderDetail);
  }

  /**
   * Answer a message from the research desk, from the order. The API sends
   * it as the shared mailbox, threaded on the message named, and records
   * it on the order as an outbound message; the detail comes back with it.
   * A 503 (`kind: 'reply_not_configured'`) means this environment has no
   * send token; a 502 (`send_failed`) means the send failed and nothing
   * was recorded. A plain call, like close: no provider outcome is in doubt.
   */
  async replyToRegistryOrder(
    orderId: string,
    request: RegistryOrderReplyRequest
  ): Promise<RegistryOrderDetail> {
    const body: Record<string, unknown> = {
      in_reply_to: request.in_reply_to,
      text: request.text.trim(),
    };
    if (request.quote) {
      body.quote = request.quote;
    }
    const response = await this.instance.post(
      `/registry-documents/orders/${encodeURIComponent(orderId)}/reply`,
      body,
      { validateStatus: () => true }
    );
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw registryOrderErrorFor(response.status, response.data, 'reply');
    }
    return transformToRegistryOrderDetail(response.data as ApiRegistryOrderDetail);
  }

  /**
   * Admin: start reading an order's delivered documents into Corporate
   * Information, Financial Information and a section per other document.
   * The API answers 202 with the run's state at once and reads in the
   * background; the order's detail carries the state as it moves, and
   * `getRegistryDigest` has the sections once it is complete. A refusal is typed for the digest call:
   * `digest_in_progress` (a run is already reading, so the screen is
   * stale), `no_documents`, `digest_disabled` (off in this environment).
   */
  async startRegistryDigest(orderId: string): Promise<RegistryDigestStatus> {
    const response = await this.instance.post(
      `/registry-documents/orders/${encodeURIComponent(orderId)}/digest`,
      null,
      { validateStatus: () => true }
    );
    this.throwDigestFailure(response);
    return transformToRegistryDigestStatus(response.data as ApiRegistryDigestStatus);
  }

  /** Admin: the extraction's state and, once a run has completed, the
   * digest itself. 404 (`kind: 'not_found'`) for an order nobody has
   * digested. */
  async getRegistryDigest(orderId: string): Promise<RegistryDigestResult> {
    const response = await this.instance.get(
      `/registry-documents/orders/${encodeURIComponent(orderId)}/digest`,
      { validateStatus: () => true }
    );
    this.throwDigestFailure(response);
    return transformToRegistryDigestResult(response.data as ApiRegistryDigestResponse);
  }

  /** Admin: a short-lived link to the digest as a Word file laid out like
   * the research team's templates, with the name to save it under.
   * 404 until a run has completed. */
  async getRegistryDigestDocx(orderId: string): Promise<RegistryDigestDocx> {
    const response = await this.instance.get(
      `/registry-documents/orders/${encodeURIComponent(orderId)}/digest/docx`,
      { validateStatus: () => true }
    );
    this.throwDigestFailure(response);
    const body = response.data as ApiRegistryDigestDocx;
    return { url: body.url, filename: body.filename };
  }

  /** Admin: the digest rendered as report markdown, one section per
   * document under a "Registry documents" heading, every string already
   * escaped: what "Copy for Word" pastes. 404 until a run has completed. */
  async getRegistryDigestMarkdown(orderId: string): Promise<RegistryDigestMarkdown> {
    const response = await this.instance.get(
      `/registry-documents/orders/${encodeURIComponent(orderId)}/digest/markdown`,
      { validateStatus: () => true }
    );
    this.throwDigestFailure(response);
    return transformToRegistryDigestMarkdown(response.data as ApiRegistryDigestMarkdown);
  }

  /**
   * Admin: append the completed digest to the report as a new content
   * version, whether the report is a Word file or Hub-written markdown
   * (`report_content.kind`). Nothing already in the report is altered:
   * the block goes in place of the one an earlier add left
   * (`replaced_block`), under the report's existing "Registry documents"
   * section (`inside_section`), before its appendix or sources heading
   * (`before_appendix`), before the footer of a Hub-written report
   * (`before_footer`), or at the end. `expectedContentVersion` is the
   * version the dry run built from: a report that moved on since is
   * refused (`content_changed`) rather than overwritten. The 409s are
   * typed by code (`digest_not_completed`, `report_stage`,
   * `report_no_content`, `report_busy` while feedback is in flight,
   * `content_changed`, `markers_damaged`); a 422 means the stored file is
   * not a readable Word document.
   */
  async addRegistryDigestToReport(
    orderId: string,
    body: { expectedContentVersion?: number | null }
  ): Promise<RegistryDigestAddedToReport> {
    const response = await this.postRegistryDigestAddToReport(orderId, {
      dry_run: false,
      ...(typeof body.expectedContentVersion === 'number'
        ? { expected_content_version: body.expectedContentVersion }
        : {}),
    });
    return transformToRegistryDigestAddedToReport(
      response.data as ApiRegistryDigestAddedToReportResponse
    );
  }

  /** Admin: the same insertion as a dry run. Every refusal of the write
   * applies; nothing is written. The answer says where the block would
   * go and whether the report's approval would be withdrawn. */
  async previewRegistryDigestAddToReport(
    orderId: string
  ): Promise<RegistryDigestAddToReportPreview> {
    const response = await this.postRegistryDigestAddToReport(orderId, { dry_run: true });
    return transformToRegistryDigestAddToReportPreview(
      response.data as ApiRegistryDigestAddToReportPreview
    );
  }

  private async postRegistryDigestAddToReport(
    orderId: string,
    body: Record<string, unknown>
  ): Promise<{ status: number; data: unknown }> {
    const response = await this.instance.post(
      `/registry-documents/orders/${encodeURIComponent(orderId)}/digest/add-to-report`,
      body,
      { validateStatus: () => true }
    );
    this.throwDigestFailure(response);
    return response;
  }

  /** A digest call that failed, typed for the digest call. Outside the
   * interceptor, like the other order calls, so the 409 code reaches the
   * caller instead of a flattened string. */
  private throwDigestFailure(response: { status: number; data: unknown }): void {
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw registryOrderErrorFor(response.status, response.data, 'digest');
    }
  }

  /** Admin: a page of the emails from the research desk that reached no
   * order, with the orders the API suggests for each. Conditional on
   * `options.etag`; each view and page is its own token space. */
  async listRegistryInboundMail(
    query: RegistryInboundQuery = {},
    options?: ConditionalRequestOptions
  ): Promise<ConditionalResult<RegistryInboundPage>> {
    return this.getConditional(
      '/registry-documents/inbound',
      { view: query.view, page: query.page, page_size: query.pageSize },
      options,
      (raw) => transformToRegistryInboundPage(raw as ApiRegistryInboundPage)
    );
  }

  /**
   * Attach an unmatched email to an order: the email and its files move
   * onto the order, which comes back as its detail. The order's status is
   * not moved. A refusal is typed by `registryOrderErrorFor` for the
   * attach call: `already_resolved`, `not_prepared` (the order was never
   * sent), `not_found`. A plain call, like close: no provider is waited on.
   */
  async attachRegistryInboundMail(rowId: string, orderId: string): Promise<RegistryOrderDetail> {
    const response = await this.instance.post(
      `/registry-documents/inbound/${encodeURIComponent(rowId)}/attach`,
      { order_id: orderId },
      { validateStatus: () => true }
    );
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw registryOrderErrorFor(response.status, response.data, 'attach');
    }
    return transformToRegistryOrderDetail(response.data as ApiRegistryOrderDetail);
  }

  /** Dismiss an unmatched email, with an optional note: it stays in the
   * mailbox and the ledger and stops counting. The row comes back resolved. */
  async dismissRegistryInboundMail(
    rowId: string,
    note?: string | null
  ): Promise<RegistryInboundRow> {
    const response = await this.instance.post(
      `/registry-documents/inbound/${encodeURIComponent(rowId)}/dismiss`,
      { note: note?.trim() || null },
      { validateStatus: () => true }
    );
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw registryOrderErrorFor(response.status, response.data, 'attach');
    }
    return transformToRegistryInboundRow(response.data as ApiRegistryInboundRow);
  }

  /** A search or catalogue read that failed: outside the interceptor, so a
   * 502 keeps the provider's reason instead of collapsing to "Server error"
   * in production — the analyst needs to know it is Armadillo, not Nexus. */
  private throwProviderRead(response: { status: number; data: unknown }): void {
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw new Error(
        detailMessage(
          (response.data as { detail?: unknown } | undefined)?.detail,
          `Error ${response.status}`
        )
      );
    }
  }

  /** Run an order call outside the response interceptor, so its outcome
   * body reaches the caller as a typed error rather than a string. */
  private async orderCall(
    send: () => Promise<{ status: number; data: unknown }>
  ): Promise<RegistryOrderDetail> {
    let response: { status: number; data: unknown };
    try {
      response = await send();
    } catch (err) {
      throw registryOrderTransportError(err);
    }
    if (response.status === 401) {
      throw new Error('Unauthorized');
    }
    if (response.status >= 400) {
      throw registryOrderErrorFor(response.status, response.data);
    }
    return transformToRegistryOrderDetail(response.data as ApiRegistryOrderDetail);
  }
}

export const vnextApiClient = new VnextApiClient();
